import { Fragment, useEffect, useMemo, useState, type ReactNode } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useParams, useNavigate, Navigate, Link } from 'react-router-dom';
import { api, API_BASE_URL } from '../../lib/api';
import { useAuthStore, useAuthStore as useAuth } from '../../stores/auth.store';
import { useModalStore } from '../../stores/modal.store';
import { toast } from '../../stores/toast.store';
import { confirm as confirmDialog } from '../../stores/confirm.store';
import { Icon, type IconName } from '../../components/ui/Icon';
import { ActionsMenu } from '../../components/ui/ActionsMenu';
import { Breadcrumb } from '../../components/ui/Breadcrumb';
import { Chip, statusTone } from '../../components/ui/Chip';
import { DetailSkeleton } from '../../components/ui/Skeleton';
import { EmptyState } from '../../components/ui/EmptyState';
import { Avatar } from '../../components/ui/Avatar';
import { AnimatedNumber } from '../../components/ui/AnimatedNumber';
import { ProgressRing } from '../../components/ui/ProgressRing';
import { MoneyThread } from '../../components/ui/MoneyThread';
import { MobileFilters } from '../../components/ui/MobileFilters';
import { MobileSheet } from '../../components/ui/MobileSheet';
import { DesktopOnly } from '../../components/ui/DesktopOnly';
import { useIsMobile } from '../../hooks/useIsMobile';
import { fmtEur, fmtDate, fmtDateShort, calcMargin } from '../../lib/utils';
import { useKanbanDnd } from '../../lib/dnd';
import { useUrlState } from '../../hooks/useUrlState';
import { DIVISION_LABEL, type Division } from '@masamune/shared';
import { LavoriTab } from './LavoriTab';
import { ProgettoMobile } from './ProgettoMobile';
import './projects.css';

const BASE_API = API_BASE_URL;

/**
 * Da dove si torna indietro. Prima queste briciole portavano a
 * `/divisions/marketing`, che oggi e' solo un vecchio indirizzo che rimanda a
 * `/progetti`: la scheda diceva "Digital Marketing" mentre la sezione in cui si
 * sta si chiama "Progetti", e il menu a sinistra aveva acceso Progetti. Adesso
 * la briciola dice il nome vero della sezione e ci riporta dentro, gia'
 * filtrata sulla divisione da cui si arrivava.
 */
const listaProgetti = (division: Division) => `/progetti?divisione=${division}`;

/**
 * Gli stati di un task, in italiano. Stavano dentro TasksTab, e l'Overview
 * scriveva invece `status.replace('_',' ')`: la stessa task era "IN PROGRESS"
 * nella panoramica e "In corso" nella scheda Task, a un clic di distanza.
 */
const TASK_STATUS_LABEL: Record<string, string> = {
  TODO: 'To do',
  IN_PROGRESS: 'In corso',
  REVIEW: 'Review',
  BLOCKED: 'Bloccati',
  DONE: 'Done',
};

function useToggleTaskDone() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, done }: { id: string; done: boolean }) =>
      api.patch(`/tasks/${id}`, { status: done ? 'DONE' : 'TODO' }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['project'] }),
  });
}

function openTask(id: string) {
  useModalStore.getState().open('taskDetail', { taskId: id });
}

/** Mini drop-target tra le card del kanban — visibile solo durante un drag. */
function DropZone<S extends string>({ dnd, status, position, visible }: {
  dnd: ReturnType<typeof useKanbanDnd<S>>;
  status: S;
  position: number;
  visible: boolean;
}) {
  const active = dnd.isOverZone(status, position);
  if (!visible) return null;
  return (
    <div
      {...dnd.dropZoneProps(status, position)}
      className={`kanban-dropzone${active ? ' is-over' : ''}`}
    />
  );
}

export interface ProjectFull {
  id: string; code: string; name: string;
  client: { id: string; legalName: string; shortName: string };
  /**
   * La trattativa da cui è nato: "venduto a Z euro il giorno tale".
   * È null per i progetti che non vengono da nessuna trattativa (lavori
   * interni, piccoli interventi sui clienti storici): il legame è facoltativo.
   */
  deal: {
    id: string; title: string;
    pipeline: { slug: string; name: string; kind: string };
    stage: { name: string; outcome: string };
    owner: { id: string; firstName: string; lastName: string; initials: string } | null;
    amount: number;
    expectedCloseDate: string | null;
  } | null;
  /** Le fatture del CLIENTE (non solo quelle agganciate a questo progetto). */
  clientInvoices?: Array<{ id: string; number: string | null; total: string; status: string; issueDate: string | null; projectId: string | null }>;
  /**
   * Gli abbonamenti del CLIENTE. Non sono il valore di questo progetto e non
   * vanno sommati ad esso: un canone è una sottoscrizione venduta sopra la
   * commessa (la gestione pratica di un voucher, un hosting, un retainer) e
   * vive nei ricavi ricorrenti. Stanno qui perché è in questa scheda che si
   * capisce quanto vale davvero un cliente.
   */
  clientSubscriptions?: Array<{ id: string; name: string; amountNet: number; cycle: string; startDate: string; endDate: string | null; active: boolean }>;
  /**
   * I canoni che fatturano QUESTO progetto, che è un'altra cosa dagli
   * abbonamenti qui sopra. Qui il canone è la RATA della commessa: le sue
   * occorrenze sommate fanno il valore del progetto, quindi si legge insieme
   * ad esso e non si somma mai al ricavo. Sono più d'uno quando il contratto
   * cambia importo per strada (Villa Fago: pieno fino a ottobre, ridotto poi).
   * Vuoto sui progetti a corpo: non è un errore, vuol dire che dietro non c'è
   * un canone ma delle fatture a stato avanzamento.
   */
  projectRecurring?: Array<{ id: string; name: string; amountNet: number; cycle: string; startDate: string; endDate: string | null; active: boolean; nextDueDate: string }>;
  /** Le consegne di Design di QUESTO progetto: sono il suo lavoro, non una sezione a parte. */
  deliverables?: Array<{ id: string; title: string; type: string; status: string; deadline: string | null; revisions: number; figmaUrl: string | null; owner: { firstName: string; lastName: string; initials: string } | null }>;
  division: Division; model: string; status: string; phase: string; progress: number;
  pm: { firstName: string; lastName: string; initials: string } | null;
  startDate: string | null; deadline: string | null;
  revenue: number; cost?: number; marginAbs?: number; marginPct?: number;
  riskLevel: string; marginThreshold: number;
  repositoryUrl: string | null; stagingUrl: string | null;
  /**
   * Da dove vengono i numeri di questa scheda e, soprattutto, che cosa non
   * sappiamo. Sui progetti ricostruiti dai preventivi e dalle fatture è la
   * differenza fra un campo vuoto che sembra una dimenticanza e un campo vuoto
   * di cui si legge il motivo.
   */
  notes: string | null;
  team: Array<{ id: string; role: string; hourlyRate?: number; hoursPlanned: number; isInternal: boolean; user: any; supplier: any }>;
  milestones: Array<{ id: string; label: string; dueDate: string; done: boolean; active: boolean; order: number }>;
  tasks: Array<{
    id: string; title: string; status: string; priority: string; dueDate: string | null; assignee: any;
    estimateHours: string | null; spentHours: string; tag: string | null; blockedReason: string | null;
    /** Lavoro previsto dal preventivo: vedi LavoriTab. */
    inScope?: boolean; quoteRef?: string | null;
    blockedBy?: 'CLIENTE' | 'NOI' | null; blockedSince?: string | null; completedAt?: string | null;
  }>;
  documents: Array<{ id: string; name: string; docGroup: string; version: string | null; mime: string; size: number; uploader: any; createdAt: string }>;
  communications: Array<{ id: string; kind: string; subject: string; body: string | null; tag: string | null; occurredAt: string; durationMin: number | null; author: any; fromText: string | null; toText: string | null }>;
  salPayments: Array<{ id: string; label: string; amount: string; dueDate: string | null; status: string }>;
  changeRequests: Array<{ id: string; label: string; amount: string; date: string | null; status: string }>;
  invoices: Array<{ id: string; number: string | null; total: string; status: string; issueDate: string | null }>;
  activity: Array<{ id: string; kind: string; message: string; actor: any; createdAt: string }>;
  alerts: Array<{ id: string; type: string; message: string }>;
  costLines?: Array<{ id: string; category: string; description: string; amount: string }>;
  automations?: Array<{ id: string; name: string; enabled: boolean }>;
  tasksCount: number;
  course?: { id: string; name: string; type: string; status: string } | null;
}

/** Le etichette delle consegne, in italiano: nel DB stanno in inglese. */
const DELIVERABLE_TIPO: Record<string, string> = {
  DESIGN_SYSTEM: 'Design system',
  PROTOTYPE: 'Prototipo',
  BRAND_ASSET: 'Asset brand',
  SOCIAL_ASSET: 'Asset social',
  WEB_PAGE: 'Pagina web',
  PRESENTATION: 'Presentazione',
  OTHER: 'Altro',
};

const DELIVERABLE_STATO: Record<string, string> = {
  BRIEF: 'Brief', DRAFT: 'Bozza', REVIEW: 'In revisione', APPROVED: 'Approvato', DELIVERED: 'Consegnato',
};

type Tab = 'overview' | 'lavori' | 'task' | 'timeline' | 'documenti' | 'diario' | 'gestione';

const TABS: { k: Tab; l: string }[] = [
  // "Panoramica", come la prima scheda di Abbonamenti: era l'unica linguetta
  // "Overview" rimasta in un'applicazione scritta in italiano. La chiave resta
  // `overview`, quindi i link con ?tab=overview continuano a funzionare.
  { k: 'overview',  l: 'Panoramica' },
  // Quello che abbiamo promesso al cliente e a che punto è (Andrea, 6/10/2026).
  // Subito dopo la Panoramica: è la domanda che si fa chi apre un progetto.
  { k: 'lavori',    l: 'Lavori previsti' },
  { k: 'task',      l: 'Task' },
  { k: 'timeline',  l: 'Timeline' },
  { k: 'documenti', l: 'Documenti' },
  { k: 'diario',    l: 'Diario' },
  { k: 'gestione',  l: 'Gestione' },
];

/** Alias dei vecchi nomi tab (deep-link/bookmark pre-ristrutturazione). */
const TAB_ALIASES: Record<string, Tab> = {
  comunicazioni: 'diario',
  attivita: 'diario',
  team: 'gestione',
  economics: 'gestione',
  budget: 'gestione',
  margini: 'gestione',
  marginalita: 'gestione',
};

/**
 * Sul telefono la scheda è ProgettoMobile: una pagina sola che dice di chi è,
 * a che punto è e cosa c'è da fare; le linguette diventano sezioni che si
 * aprono da lì (stesso ?tab=). Stessa chiamata e stessa cache. Sul computer
 * resta questa, identica.
 */
export function ProjectDetailPage() {
  const isMobile = useIsMobile();
  return isMobile ? <div className="page"><ProgettoMobile /></div> : <ProjectDetailDesktop />;
}

function ProjectDetailDesktop() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const qc = useQueryClient();
  const openModal = useModalStore((s) => s.open);
  const user = useAuthStore((s) => s.user);
  const showMargin = !!user?.role.canSeeMargin;
  // Tab sincronizzata con l'URL (?tab=…) — i vecchi nomi vengono rimappati via alias.
  const [rawTab, setTab] = useUrlState<string>('tab', 'overview');
  const tab: Tab = TABS.some((t) => t.k === rawTab) ? (rawTab as Tab) : (TAB_ALIASES[rawTab] ?? 'overview');
  // CTA header "Nuovo task" → quick-add inline nella tab Task. Ogni click è una
  // richiesta nuova (timestamp): il quick-add si riattiva anche a tab già aperta.
  const [quickAddReq, setQuickAddReq] = useState(0);

  const { data: p, isLoading, isError } = useQuery({
    queryKey: ['project', id],
    queryFn: () => api.get<ProjectFull>(`/projects/${id}`),
    enabled: !!id,
    retry: false,
  });

  const delProject = useMutation({
    mutationFn: () => api.delete(`/projects/${id}`),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['projects'] });
      qc.invalidateQueries({ queryKey: ['dashboard'] });
      toast.ok('Progetto eliminato', p?.name);
      navigate(-1);
    },
    onError: (e: any) => toast.alert('Errore', e?.message ?? 'Riprova'),
  });

  if (!id) return <Navigate to="/" replace />;
  if (isError) return (
    <div className="page">
      <Breadcrumb items={[{ label: 'Progetti', to: '/progetti' }, { label: 'Progetto' }]} />
      <div className="max-w-[480px] mx-auto py-20 text-center">
        <h1 className="t-h1">Progetto non trovato</h1>
        <div className="mute mt-2">L'ID richiesto non esiste o non hai i permessi per vederlo.</div>
      </div>
    </div>
  );
  if (isLoading || !p) return <DetailSkeleton header="chips" />;

  return (
    // m-detail: sotto i 768px le griglie a due colonne di questa scheda vanno a
    // colonna sola (os-planning/mobile.md §7). pj-detail: le poche regole di
    // questa sezione, in projects.css. Sul computer nessuna delle due fa nulla.
    <div className="page m-detail pj-detail">
      <Breadcrumb items={[
        { label: 'Progetti', to: listaProgetti(p.division) },
        { label: p.code },
      ]} />
      {/* RIFACIMENTO EDITORIALE: solo l'header — titolo grande e leggero, hairline aurora */}
      <div className="page-ed-hd flex items-end justify-between gap-4">
        <div className="min-w-0 max-w-[760px]">
          {/* L1 protagonista: il nome, tronca se lungo */}
          <h1 className="ed-title truncate">{p.name}</h1>
          {/* Meta unica e calma: divisione · modello · cliente · PM (niente codice/monogramma).
              flex-wrap: .detail-meta e' un flex senza ritorno a capo, e su 390px
              spezzava le parole invece dei dati ("Digital / Product"). */}
          <div className="detail-meta mt-2 flex-wrap">
            <span>{DIVISION_LABEL[p.division]}</span>
            <span className="mute-2">·</span>
            <span className="lowercase">{p.model.replace(/_/g, ' ')}</span>
            <span className="mute-2">·</span>
            <span><span className="mute-2 mr-1.5">Cliente</span><Link to={`/clients/${p.client.id}`} className="v hover:underline underline-offset-2 m-hit">{p.client.legalName}</Link></span>
            <span className="mute-2">·</span>
            <span><span className="mute-2 mr-1.5">PM</span><span className="v">{p.pm ? `${p.pm.firstName[0]}. ${p.pm.lastName}` : '—'}</span></span>
          </div>
          {/* Da dove viene: una riga sola per tornare alla trattativa e leggere
              com'era stata venduta. Manca quando il progetto non nasce da una
              trattativa, che è normale: il legame è facoltativo. */}
          {p.deal && (
            <div className="detail-meta mt-1.5 flex-wrap">
              <span className="mute-2">Nato dalla trattativa</span>
              <Link to={`/trattative/${p.deal.id}`} className="v hover:underline underline-offset-2 m-hit">{p.deal.title}</Link>
              <span className="mute-2">·</span>
              <span>venduto a <span className="v mono">€ {fmtEur(p.deal.amount, true)}</span></span>
              {p.deal.expectedCloseDate && (
                <>
                  <span className="mute-2">·</span>
                  <span className="mono mute">{fmtDate(p.deal.expectedCloseDate)}</span>
                </>
              )}
            </div>
          )}
          {/* Alert: riga dedicata SOTTO, mai a competere col titolo; max 1 + contatore */}
          {p.alerts.length > 0 && (
            <div className="flex gap-1.5 mt-2.5 items-center">
              <Chip tone="alert"><Icon name="alert" size={10} /> {p.alerts[0]!.message}</Chip>
              {p.alerts.length > 1 && <span className="mute-2 text-[12px]">+{p.alerts.length - 1}</span>}
            </div>
          )}
        </div>
        {/* items-center: con due comandi di altezza diversa (il menu ghost e il
            bottone primario) senza allineamento uno dei due ballava di un paio
            di pixel rispetto all'altro. */}
        <div className="flex gap-2 pb-1 items-center d-only">
          <ActionsMenu
            variant="ghost"
            items={[
              {
                key: 'quote',
                label: 'Crea preventivo',
                icon: 'file',
                // "Esporta preventivo" → crea preventivo per questo progetto via wizard
                onClick: () => openModal('newQuote', { clientId: p.client.id, projectId: p.id }),
              },
              {
                key: 'repo',
                label: 'Apri repository',
                icon: 'link',
                hidden: !p.repositoryUrl,
                onClick: () => window.open(p.repositoryUrl!, '_blank', 'noopener,noreferrer'),
              },
              {
                key: 'delete',
                label: 'Elimina progetto',
                icon: 'trash',
                danger: true,
                onClick: async () => {
                  if (await confirmDialog({
                    title: `Eliminare ${p.name}?`,
                    body: 'Il progetto sparisce dalle liste. Task, milestone, SAL e documenti collegati restano nel sistema. Operazione reversibile dal database.',
                    danger: true,
                    confirmLabel: 'Elimina progetto',
                  })) delProject.mutate();
                },
              },
            ]}
          />
          {/* Niente modale qui: porta alla tab Task e apre il quick-add inline.
              Il modale completo resta raggiungibile da Diario ("Crea task da qui").
              d-only: sul telefono lo stesso comando sta nella barra in fondo. */}
          <button className="btn btn-primary" onClick={() => { setTab('task'); setQuickAddReq(Date.now()); }}><Icon name="plus" /> Nuovo task</button>
        </div>
      </div>

      {/* L'avviso "la gestione avanzata si fa dal computer" stava qui, sopra le
          linguette di OGNI scheda: sul telefono erano 220px prima di arrivare a
          qualunque dato. Ora sta in fondo a Gestione, dov'è la gestione. */}
      <div className="page-tabs">
        {TABS.map((t) => (
          <div key={t.k} className="page-tab" aria-selected={tab === t.k} onClick={() => setTab(t.k)}>
            {t.l}
          </div>
        ))}
      </div>

      {tab === 'overview'  && <OverviewTab p={p} showMargin={showMargin} onNavigate={setTab} />}
      {tab === 'lavori'    && <LavoriTab projectId={p.id} tasks={p.tasks} communications={p.communications} />}
      {tab === 'task'      && <TasksTab p={p} quickAddReq={quickAddReq} />}
      {tab === 'timeline'  && <TimelineTab p={p} />}
      {tab === 'documenti' && <DocsTab p={p} />}
      {tab === 'diario'    && <DiarioTab p={p} initialSection={rawTab === 'attivita' ? 'log' : 'comms'} />}
      {tab === 'gestione'  && <GestioneTab p={p} showMargin={showMargin} />}

      {/* L'azione principale, sul telefono, sta in fondo (os-planning/mobile.md §4).
          Nei Lavori previsti la barra ce l'ha la scheda (preventivo e resoconto). */}
      {tab !== 'lavori' && (
        <div className="m-actionbar m-only">
          <button className="btn btn-primary" onClick={() => { setTab('task'); setQuickAddReq(Date.now()); }}>
            <Icon name="plus" size={12} /> Nuovo task
          </button>
        </div>
      )}
    </div>
  );
}

// =====================================================================
// OVERVIEW
// =====================================================================
function OverviewTab({ p, showMargin, onNavigate }: { p: ProjectFull; showMargin: boolean; onNavigate: (tab: Tab) => void }) {
  const toggleDone = useToggleTaskDone();
  // Solo la PROSSIMA milestone non completata: il quadro completo vive in Timeline.
  const nextMilestone = [...p.milestones].sort((a, b) => a.order - b.order).find((m) => !m.done);
  const nextDueMs = nextMilestone?.dueDate ? new Date(nextMilestone.dueDate).getTime() : NaN;
  const nextDays = Number.isFinite(nextDueMs) ? Math.ceil((nextDueMs - Date.now()) / 86400000) : null;
  const openTasks = p.tasks.filter((t) => t.status !== 'DONE');
  // Con i lavori previsti caricati l'avanzamento si calcola da loro (lato API):
  // sotto il numero si dice da dove viene, e il numero porta alla scheda.
  const previsti = p.tasks.filter((t) => t.inScope);
  const previstiFatti = previsti.filter((t) => t.status === 'DONE').length;
  const [notaAperta, setNotaAperta] = useState(false);

  // Filo dei soldi: ricostruito SOLO dai cross-ref presenti nel payload (niente
  // fetch extra). Il preventivo non è nel payload progetto → step omesso; le
  // fatture collegate ci sono → Fattura + Incasso. Con un solo step certo
  // (Progetto) il thread non si renderizza: una catena da un anello non è una catena.
  const fattureValide = (p.invoices ?? []).filter((i) => i.status !== 'ANNULLATA');
  const fatturato = fattureValide.reduce((a, i) => a + (Number(i.total) || 0), 0);
  const incassato = fattureValide
    .filter((i) => i.status === 'PAGATA')
    .reduce((a, i) => a + (Number(i.total) || 0), 0);
  const moneySteps = [
    { key: 'progetto', label: 'Progetto corrente', sub: `${p.progress}%`, state: 'current' as const },
    ...(fattureValide.length > 0
      ? [
          {
            key: 'fattura',
            label: 'Fattura',
            sub: `€ ${fmtEur(fatturato)}`,
            state: p.revenue > 0 && fatturato >= p.revenue ? ('done' as const) : ('current' as const),
          },
          {
            key: 'incasso',
            label: 'Incasso',
            sub: incassato > 0 ? `€ ${fmtEur(incassato)}` : undefined,
            state: incassato > 0 && incassato >= fatturato ? ('done' as const) : ('future' as const),
          },
        ]
      : []),
  ];
  return (
    <>
    {/* Riassunto "a colpo d'occhio": numeri NUDI sul foglio (stat-band), zero scatole */}
    <div className="stat-band">
      <div>
        <div className="st-label">Avanzamento</div>
        <div className="st-value"><AnimatedNumber value={p.progress} /><span className="u">%</span></div>
        {previsti.length > 0
          ? <button type="button" className="st-delta lv-link m-hit" onClick={() => onNavigate('lavori')}>{previstiFatti} di {previsti.length} lavori previsti fatti</button>
          : <div className="st-delta">{p.phase}</div>}
      </div>
      <div>
        <div className="st-label">Task aperti</div>
        <div className="st-value"><AnimatedNumber value={openTasks.length} /></div>
        <div className="st-delta">{p.tasks.filter((t) => t.status === 'DONE').length} chiusi</div>
      </div>
      <div>
        <div className="st-label">Prossima scadenza</div>
        <div className="st-value">
          {nextDays == null ? '—' : <><AnimatedNumber value={Math.abs(nextDays)} /><span className="u">gg{nextDays < 0 ? ' fa' : ''}</span></>}
        </div>
        {/* Rosso solo se davvero in ritardo: è un warning reale, non decorazione */}
        <div className="st-delta" style={nextDays != null && nextDays < 0 ? { color: 'var(--alert)' } : undefined}>{nextMilestone?.label ?? 'Nessuna milestone'}</div>
      </div>
      <div>
        <div className="st-label">Team</div>
        <div className="st-value"><AnimatedNumber value={p.team.length} /></div>
        <div className="st-delta">{p.team.filter((t) => t.isInternal).length} interni</div>
      </div>
    </div>
    <div className="grid gap-6" style={{ gridTemplateColumns: '2fr 1fr' }}>
      <div className="col gap-6">
        {/* Da dove vengono questi numeri. Sta PRIMA di tutto il resto perché su
            un progetto ricostruito dai documenti è la domanda che uno si fa
            guardando il valore e la scadenza: "chi l'ha deciso?". E perché un
            campo lasciato vuoto di proposito, senza il motivo scritto accanto,
            in due settimane diventa un campo che qualcuno riempie a caso.
            `pre-wrap`: il testo è scritto a capoversi, e vanno tenuti. */}
        {!!p.notes && (
          <div className="ed-section">
            <div className="data-hd">
              <span className="t-micro">Da dove vengono questi dati</span>
            </div>
            {/* pj-nota-chiusa vale solo sotto i 768px (projects.css): sul computer la nota resta intera. */}
            <div className={`text-[13px] mute${notaAperta ? '' : ' pj-nota-chiusa'}`} style={{ whiteSpace: 'pre-wrap', lineHeight: 1.6, maxWidth: 760 }}>
              {p.notes}
            </div>
            <button type="button" className="btn btn-ghost pj-nota-btn m-only" onClick={() => setNotaAperta((v) => !v)}>
              {notaAperta ? 'Mostra meno' : 'Leggi tutta la nota'}
            </button>
          </div>
        )}

        {/* Kicker-block senza card: la milestone vive nuda sul foglio */}
        <div className="ed-section">
          <div className="data-hd">
            <span className="t-micro">Prossima milestone</span>
            <button className="btn btn-sm btn-ghost" onClick={() => onNavigate('timeline')}>Vedi timeline <Icon name="chevronR" size={12} /></button>
          </div>
          {nextMilestone ? (
            <div className="flex items-center gap-3 py-1">
              <div className="w-3 h-3 rounded-full flex-shrink-0"
                style={{
                  background: 'var(--surface)',
                  border: nextMilestone.active ? '2px solid var(--accent)' : '1px solid var(--line-2)',
                }} />
              <div className="flex-1 min-w-0">
                <div className=" font-semibold truncate">{nextMilestone.label}</div>
                <div className="mute mono text-[12px]">{fmtDate(nextMilestone.dueDate)}{nextMilestone.active ? ' · In corso' : ''}</div>
              </div>
              <Chip tone={nextDays == null ? 'ghost' : nextDays < 0 ? 'alert' : nextDays < 7 ? 'warn' : 'ghost'}>
                {nextDays == null ? '—' : nextDays < 0 ? `${Math.abs(nextDays)} gg in ritardo` : `tra ${nextDays} gg`}
              </Chip>
            </div>
          ) : (
            <div className="text-center py-4 text-[13px] mute">
              {p.milestones.length ? 'Tutte le milestone sono completate' : 'Nessuna milestone'}
            </div>
          )}
        </div>

        {/* Tabella full-bleed: kicker + conteggio, stessa CTA a destra */}
        <div className="ed-section">
          <div className="data-hd">
            <div className="flex items-center gap-2">
              <span className="t-micro">Task aperti</span>
              <span className="mono mute text-[12px]">{openTasks.length}</span>
            </div>
            <button className="btn btn-sm btn-ghost" onClick={() => onNavigate('task')}>Vedi tutto <Icon name="chevronR" size={12} /></button>
          </div>
          {/* tbl--card: sotto i 768px ogni riga diventa una scheda. Sul computer
              non cambia niente, data-m e data-l sono attributi. */}
          <table className="tbl tbl--ed tbl--card">
            <thead>
              <tr>
                <th style={{ width: 30 }} /><th>Titolo</th><th>Assegnato</th><th>Stato</th><th>Priorità</th><th className="num">Scadenza</th>
              </tr>
            </thead>
            <tbody>
              {openTasks.slice(0, 6).map((t) => (
                <tr key={t.id} onClick={() => openTask(t.id)}>
                  {/* La casella di spunta e' un gesto rapido: sul telefono si
                      prende una riga sua invece di sparire in una colonna. */}
                  <td data-m="off" onClick={(e) => { e.stopPropagation(); toggleDone.mutate({ id: t.id, done: t.status !== 'DONE' }); }}>
                    <div className="w-3.5 h-3.5 border border-line-2 rounded-sm bg-white grid place-items-center cursor-pointer hover:border-ink"
                      style={{ background: t.status === 'DONE' ? 'var(--ink)' : undefined }}>
                      {t.status === 'DONE' && <Icon name="check" size={8} className="text-white" />}
                    </div>
                  </td>
                  <td data-m="hd" className={t.status === 'DONE' ? 'line-through text-mute' : ''}>{t.title}</td>
                  {/* Il trattino quando non c'e' nessuno: in scheda, sul
                      telefono, la cella vuota lasciava l'etichetta ASSEGNATO
                      sospesa sul niente. Qui "non assegnato" e' un dato. */}
                  <td data-m="key" data-l="Assegnato">{t.assignee
                    ? <div className="flex gap-1.5 items-center"><Avatar initials={t.assignee.initials} size={20} />{t.assignee.firstName[0]}. {t.assignee.lastName}</div>
                    : <span className="mute-2">—</span>}</td>
                  {/* Stessa etichetta della scheda Task: prima qui usciva il
                      nome dell'enum ("IN PROGRESS") e li' "In corso".
                      nowrap come nella lista Progetti: una pastiglia spezzata a
                      meta' ("In / corso") si legge come un errore, mentre un
                      titolo che va a capo e' normale. */}
                  <td data-m="key" data-l="Stato" style={{ whiteSpace: 'nowrap' }}><Chip tone={statusTone(t.status)}>{t.status === 'BLOCKED' && t.blockedBy === 'CLIENTE' ? 'Aspettiamo il cliente' : (TASK_STATUS_LABEL[t.status] ?? t.status)}</Chip></td>
                  <td data-m="key" data-l="Priorità"><span className="inline-flex items-center gap-1.5 text-[12px] mute"><span className="w-1.5 h-1.5 rounded-full" style={{ background: t.priority === 'ALTA' ? 'var(--alert)' : t.priority === 'MEDIA' ? 'var(--warn)' : 'var(--mute-2)' }} />{t.priority.toLowerCase()}</span></td>
                  <td data-m="key" data-l="Scadenza" className="num mono mute">{fmtDate(t.dueDate)}</td>
                </tr>
              ))}
              {!openTasks.length && (
                <tr><td colSpan={6} className="text-center py-6 mute">Nessun task aperto</td></tr>
              )}
            </tbody>
          </table>
        </div>

        {/* Le consegne di Design. Stanno qui, dentro il progetto, perché sono
            il suo lavoro: prima vivevano in una pagina a parte, e un progetto
            di divisione Design non le vedeva. Se non ce ne sono, il blocco non
            si mostra: una tabella vuota in più non aiuta nessuno. */}
        {!!p.deliverables?.length && (
          <div className="ed-section">
            <div className="data-hd">
              <div className="flex items-center gap-2">
                <span className="t-micro">Consegne</span>
                <span className="mono mute text-[12px]">{p.deliverables.length}</span>
              </div>
            </div>
            {/* pj-nolink: queste righe non si aprono, quindi niente chevron. */}
            <table className="tbl tbl--ed tbl--card pj-nolink">
              <thead>
                <tr><th>Consegna</th><th>Tipo</th><th>A che punto è</th><th className="num">Revisioni</th><th className="num">Scadenza</th></tr>
              </thead>
              <tbody>
                {p.deliverables.map((d) => (
                  <tr key={d.id}>
                    <td data-m="hd">
                      <div className="font-medium">{d.title}</div>
                      {d.owner && <div className="text-[12px] mute">{d.owner.firstName} {d.owner.lastName}</div>}
                    </td>
                    <td data-m="key" data-l="Tipo">{DELIVERABLE_TIPO[d.type] ?? d.type}</td>
                    <td data-m="key" data-l="A che punto è"><Chip tone={statusTone(d.status)} dot>{DELIVERABLE_STATO[d.status] ?? d.status}</Chip></td>
                    <td data-m="key" data-l="Revisioni" className="num mono">{d.revisions}</td>
                    <td data-m="key" data-l="Scadenza" className="num mono mute">{fmtDate(d.deadline)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      <div className="col gap-6">
        {/* Anagrafica nuda: kicker + chiave-valore con hairline, niente card */}
        <div className="ed-section">
          <div className="data-hd">
            <span className="t-micro">Dettagli</span>
          </div>
          <div className="ed-kv">
            <div className="ed-kv-row"><span className="k">Modello</span><span className="v">{p.model.replace(/_/g, ' ')}</span></div>
            {p.stagingUrl && <div className="ed-kv-row"><span className="k">Staging</span><span className="v"><a className="hover:underline underline-offset-2" style={{ color: 'var(--accent)' }} href={p.stagingUrl} target="_blank" rel="noreferrer">{p.stagingUrl.replace(/^https?:\/\//, '')}</a></span></div>}
            {p.repositoryUrl && <div className="ed-kv-row"><span className="k">Repository</span><span className="v"><a className="hover:underline underline-offset-2" style={{ color: 'var(--accent)' }} href={p.repositoryUrl} target="_blank" rel="noreferrer">{p.repositoryUrl.replace(/^https?:\/\//, '')}</a></span></div>}
          </div>
        </div>

        {/* Filo dei soldi sopra il pannello margine: solo gli step certi dal payload */}
        {moneySteps.length > 1 && <MoneyThread steps={moneySteps} />}

        {showMargin && p.cost != null ? (
          <ProjectMarginPanel p={p} onDetail={() => onNavigate('gestione')} />
        ) : (
          <div className="margin-locked">
            <div className="icon"><Icon name="lock" size={14} /></div>
            <div className="font-semibold text-text mb-1">Marginalità</div>
            <div className="text-[13px]">Visibile solo al Super Admin.</div>
          </div>
        )}

        {/* Le fatture del cliente. Non solo quelle agganciate a questo
            progetto: molte nascono senza `projectId` (arrivano dal sync di
            FattureInCloud) e altrimenti da qui non si vedrebbero. Quelle di
            questo progetto si distinguono, così non si confonde "fatturato su
            questo lavoro" con "fatturato a questo cliente". */}
        {!!p.clientInvoices?.length && (
          <div className="ed-section">
            <div className="data-hd">
              <div className="flex items-center gap-2">
                <span className="t-micro">Fatture del cliente</span>
                <span className="mono mute text-[12px]">{p.clientInvoices.length}</span>
              </div>
            </div>
            <div className="ed-kv">
              {p.clientInvoices.slice(0, 6).map((f) => (
                <div key={f.id} className="ed-kv-row">
                  <span className="k">
                    {f.number ?? 'senza numero'}
                    {f.projectId === p.id && <span className="mute-2 text-[11px]"> · di questo progetto</span>}
                  </span>
                  <span className="v mono">€ {fmtEur(f.total)}</span>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* COME SI FATTURA questo progetto. Non è la stessa cosa del blocco
            qui sotto: lì ci sono altri abbonamenti dello stesso cliente, qui
            c'è la rata con cui si incassa proprio questa commessa. La riga in
            fondo dice a voce che il canone e il valore del progetto sono lo
            stesso euro, perché sommarli è l'errore che viene naturale. */}
        {!!p.projectRecurring?.length && (
          <div className="ed-section">
            <div className="data-hd">
              <div className="flex items-center gap-2">
                <span className="t-micro">Come si fattura</span>
                <span className="mono mute text-[12px]">{p.projectRecurring.length}</span>
              </div>
              <Link to="/services" className="btn btn-sm btn-ghost">Abbonamenti <Icon name="chevronR" size={12} /></Link>
            </div>
            <div className="ed-kv">
              {p.projectRecurring.map((s) => (
                <div key={s.id} className="ed-kv-row">
                  {/* Data corta: la chiave è già in maiuscoletto e con la data
                      per esteso andava a capo tre volte. Qui conta il fino a
                      quando, non il giorno preciso. */}
                  <span className="k">
                    {s.name}
                    {s.endDate
                      ? <span className="mute-2 text-[11px]"> · fino al {fmtDateShort(s.endDate)}</span>
                      : <span className="mute-2 text-[11px]"> · senza scadenza</span>}
                  </span>
                  <span className="v mono">€ {fmtEur(s.amountNet)}{s.cycle === 'MONTHLY' ? '/mese' : ''}</span>
                </div>
              ))}
            </div>
            <div className="text-[12px] mute mt-2">
              È la rata di questo progetto, non un ricavo in più: il canone genera le fatture e la previsione
              di cassa, il valore qui sopra è lo stesso denaro visto per intero. Non si sommano.
            </div>
          </div>
        )}

        {/* Gli abbonamenti del cliente. Sono ricavi ricorrenti, NON il valore
            di questo progetto: un canone si vende sopra la commessa e sommarlo
            all'imponibile conterebbe due volte gli stessi soldi. Sta scritto
            nel blocco, perché è esattamente l'errore che verrebbe naturale. */}
        {!!p.clientSubscriptions?.length && (
          <div className="ed-section">
            <div className="data-hd">
              <div className="flex items-center gap-2">
                <span className="t-micro">Abbonamenti del cliente</span>
                <span className="mono mute text-[12px]">{p.clientSubscriptions.length}</span>
              </div>
            </div>
            <div className="ed-kv">
              {p.clientSubscriptions.slice(0, 6).map((s) => (
                <div key={s.id} className="ed-kv-row">
                  <span className="k">
                    {s.name}
                    {!s.active && <span className="mute-2 text-[11px]"> · non attivo</span>}
                  </span>
                  <span className="v mono">€ {fmtEur(s.amountNet)}{s.cycle === 'MONTHLY' ? '/mese' : ''}</span>
                </div>
              ))}
            </div>
            <div className="text-[12px] mute mt-2">
              Ricavi ricorrenti: non fanno parte del valore del progetto.
            </div>
          </div>
        )}

        {/* Kicker-block senza bordi: anteprima documenti nuda sul foglio */}
        <div className="ed-section">
          <div className="data-hd">
            <div className="flex items-center gap-2">
              <span className="t-micro">Documenti</span>
              <span className="mono mute text-[12px]">{p.documents.length}</span>
            </div>
            <button className="btn btn-sm btn-ghost" onClick={() => onNavigate('documenti')}>Vedi tutti <Icon name="chevronR" size={12} /></button>
          </div>
          <div>
            {p.documents.slice(0, 4).map((d) => (
              <div key={d.id} className="flex items-center gap-2.5 py-1.5">
                <Icon name="file" size={14} className="mute" />
                <span className=" flex-1 truncate">{d.name}</span>
                <span className="mute mono text-[12px]">{d.mime.split('/')[1]?.toUpperCase()}</span>
              </div>
            ))}
            {!p.documents.length && (
              <EmptyState
                icon="file"
                title="Nessun documento"
                action={
                  <button className="btn btn-sm" onClick={() => useModalStore.getState().open('uploadDoc', { scope: 'project', scopeId: p.id })}>
                    <Icon name="upload" size={12} /> Carica il primo
                  </button>
                }
              />
            )}
          </div>
        </div>
      </div>
    </div>
    </>
  );
}

function ProjectMarginPanel({ p, onDetail }: { p: ProjectFull; onDetail?: () => void }) {
  const cost = p.cost ?? 0;
  const { abs, pct } = calcMargin(p.revenue, cost);
  return (
    <div className="margin-panel">
      <div className="margin-panel-hd">
        <span className="t-micro" style={{ color: 'var(--mute)' }}>Marginalità progetto</span>
        <div className="flex items-center gap-2.5">
          {onDetail && (
            // Il chevron come icona, non la freccia scritta a mano: e' quello
            // che fanno gli altri rimandi della scheda ("Vedi timeline ›",
            // "Vedi tutto ›"). Erano due frecce diverse nella stessa schermata.
            <button
              className="text-[12px] cursor-pointer hover:underline underline-offset-2 inline-flex items-center gap-1"
              style={{ color: 'var(--accent-text)', background: 'none', border: 'none', padding: 0 }}
              onClick={onDetail}
            >
              Dettaglio <Icon name="chevronR" size={12} />
            </button>
          )}
          <span style={{ color: 'var(--mute)', display: 'inline-flex' }}><Icon name="eye" size={14} /></span>
        </div>
      </div>
      <div className="margin-panel-bd">
        <div className="margin-row"><span className="label">Ricavi</span><span className="value">€ {fmtEur(p.revenue)}</span></div>
        <div className="margin-row"><span className="label">Costi totali</span><span className="value">€ {fmtEur(cost)}</span></div>
        {/* Senza costi non c'è nessun margine da mostrare. Il conto darebbe
            "100% sui ricavi", che si legge come un progetto perfetto e invece
            vuol dire solo che i costi non li ha ancora scritti nessuno. Si
            dice quello, e si dice dove si scrivono. */}
        {cost === 0 ? (
          <div className="margin-highlight">
            <div className="t-micro mb-1.5" style={{ color: 'var(--mute)' }}>Margine lordo</div>
            <div className="text-[13px]" style={{ color: 'var(--mute)' }}>
              Non si sa ancora: su questo progetto non ci sono costi.
              {onDetail && <> Si aggiungono dalla scheda <b>Gestione</b>.</>}
            </div>
          </div>
        ) : (
        <div className="margin-highlight">
          <div className="flex items-center gap-4">
            <span className="ring-bloom"><ProgressRing pct={pct} threshold={Number(p.marginThreshold)} tone="ink" size={78} stroke={8} /></span>
            <div className="min-w-0">
              <div className="t-micro mb-1.5" style={{ color: 'var(--mute)' }}>Margine lordo</div>
              <div className="big">€ {fmtEur(abs)}</div>
              <div className="row between mt-2 text-[13px] gap-3" style={{ color: 'var(--mute)' }}>
                <span>{pct.toFixed(1)}% sui ricavi</span>
                <span>Soglia {Number(p.marginThreshold).toFixed(0)}%</span>
              </div>
            </div>
          </div>
        </div>
        )}
        {cost > 0 && pct < 20 && (
          <div className="p-2.5 mt-2.5 rounded text-[13px]" style={{ background: 'rgba(239, 85, 70, 0.10)', border: '1px solid rgba(239, 85, 70, 0.28)', color: 'oklch(0.52 0.20 30)' }}>
            <b>Alert: sotto soglia minima aziendale.</b> Escalation a Super Admin.
          </div>
        )}
      </div>
    </div>
  );
}

// =====================================================================
// TIMELINE — Gantt vero
// =====================================================================
function TimelineTab({ p }: { p: ProjectFull }) {
  // Dipendenze critiche dietro disclosure: dettaglio tecnico, chiuso di default.
  const [showDeps, setShowDeps] = useState(false);
  // Range "naturale" del progetto basato su milestone + scadenza
  const allDates = useMemo(() => [
    p.startDate ? new Date(p.startDate) : null,
    p.deadline ? new Date(p.deadline) : null,
    ...p.milestones.map((m) => new Date(m.dueDate)),
  ].filter((d): d is Date => !!d && !Number.isNaN(d.getTime())), [p]);

  const naturalStart = useMemo(() => {
    const d = allDates.length ? new Date(Math.min(...allDates.map((x) => x.getTime()))) : new Date();
    d.setDate(d.getDate() - 14);
    return d;
  }, [allDates]);
  const naturalEnd = useMemo(() => {
    const d = allDates.length ? new Date(Math.max(...allDates.map((x) => x.getTime()))) : new Date(naturalStart.getTime() + 90 * 86400000);
    d.setDate(d.getDate() + 14);
    return d;
  }, [allDates, naturalStart]);

  // Viewport controllato
  const [viewStart, setViewStart] = useState<Date>(naturalStart);
  const [viewSpanDays, setViewSpanDays] = useState<number>(() => {
    const span = Math.ceil((naturalEnd.getTime() - naturalStart.getTime()) / 86400000);
    if (span <= 30) return 30;
    if (span <= 90) return 90;
    if (span <= 180) return 180;
    return 365;
  });

  const viewEnd = useMemo(() => new Date(viewStart.getTime() + viewSpanDays * 86400000), [viewStart, viewSpanDays]);
  const totalMs = viewEnd.getTime() - viewStart.getTime() || 1;

  const pan = (deltaDays: number) => {
    const d = new Date(viewStart);
    d.setDate(d.getDate() + deltaDays);
    setViewStart(d);
  };
  const resetView = () => {
    setViewStart(naturalStart);
    const span = Math.ceil((naturalEnd.getTime() - naturalStart.getTime()) / 86400000);
    setViewSpanDays(span <= 30 ? 30 : span <= 90 ? 90 : span <= 180 ? 180 : 365);
  };
  const setZoom = (days: number) => {
    // Mantiene il centro del viewport quando cambia zoom
    const center = new Date(viewStart.getTime() + (totalMs / 2));
    const newStart = new Date(center.getTime() - (days * 86400000) / 2);
    setViewStart(newStart);
    setViewSpanDays(days);
  };

  // Mesi visibili
  const months: { label: string; left: number }[] = [];
  const cur = new Date(viewStart.getFullYear(), viewStart.getMonth(), 1);
  while (cur <= viewEnd) {
    const pct = ((cur.getTime() - viewStart.getTime()) / totalMs) * 100;
    if (pct >= -5 && pct <= 100) {
      months.push({
        label: cur.toLocaleDateString('it-IT', { month: 'short', year: '2-digit' }),
        left: Math.max(0, pct),
      });
    }
    cur.setMonth(cur.getMonth() + 1);
  }

  const todayPct = ((Date.now() - viewStart.getTime()) / totalMs) * 100;
  const showToday = todayPct >= 0 && todayPct <= 100;

  // Lanes
  const lanes = p.milestones.map((m, i, arr) => {
    const startD = i === 0
      ? (p.startDate ? new Date(p.startDate) : new Date(arr[0]!.dueDate))
      : new Date(arr[i - 1]!.dueDate);
    const endD = new Date(m.dueDate);
    return {
      milestone: m,
      startPct: ((startD.getTime() - viewStart.getTime()) / totalMs) * 100,
      endPct: ((endD.getTime() - viewStart.getTime()) / totalMs) * 100,
    };
  });

  // Conteggio milestone fuori viewport
  const hiddenLeft = lanes.filter((l) => l.endPct < 0).length;
  const hiddenRight = lanes.filter((l) => l.startPct > 100).length;

  return (
    <div className="space-y-6">
      {/* Il Gantt e' una fascia lunga da leggere di traverso: su 390px la
          colonna delle etichette da sola si mangia metà schermo e le barre
          diventano trattini. Le prossime milestone, qui sotto, dicono la stessa
          cosa in forma di elenco (os-planning/mobile.md §8). */}
      <DesktopOnly
        cosa="Il Gantt delle milestone"
        perche="è una fascia lunga da leggere di traverso, e su uno schermo stretto le barre si riducono a trattini. Qui sotto restano le prossime milestone in elenco."
      >
      <div className="card">
        <CardHd
          icon="calendar"
          title="Gantt — milestone progetto"
          sub={
            <span className="mono">
              {viewStart.toLocaleDateString('it-IT', { day: '2-digit', month: 'short', year: 'numeric' })}
              {' → '}
              {viewEnd.toLocaleDateString('it-IT', { day: '2-digit', month: 'short', year: 'numeric' })}
            </span>
          }
          action={
          <div className="flex items-center gap-2">
            <div className="seg-control">
              <button onClick={() => setZoom(30)} aria-pressed={viewSpanDays === 30}>1M</button>
              <button onClick={() => setZoom(90)} aria-pressed={viewSpanDays === 90}>3M</button>
              <button onClick={() => setZoom(180)} aria-pressed={viewSpanDays === 180}>6M</button>
              <button onClick={() => setZoom(365)} aria-pressed={viewSpanDays === 365}>1A</button>
            </div>
            <div className="seg-control">
              <button onClick={() => pan(-Math.round(viewSpanDays / 4))} title="Indietro">←</button>
              <button onClick={resetView} title="Centra sulle milestone">⌖</button>
              <button onClick={() => pan(Math.round(viewSpanDays / 4))} title="Avanti">→</button>
            </div>
            {/* CTA secondaria: l'unica primaria della vista resta "Nuovo task" nell'header */}
            <button className="btn btn-sm" onClick={() => useModalStore.getState().open('newMilestone', { projectId: p.id })}>
              <Icon name="plus" size={12} /> Milestone
            </button>
          </div>
          }
        />
        <div className="card-bd">
          {(hiddenLeft > 0 || hiddenRight > 0) && (
            <div className="mb-3 text-[12px] mute flex items-center gap-2">
              {hiddenLeft > 0 && (
                <button className="btn btn-sm btn-ghost" onClick={() => pan(-Math.round(viewSpanDays / 2))}>
                  ← {hiddenLeft} milestone passate
                </button>
              )}
              <div className="spacer" />
              {hiddenRight > 0 && (
                <button className="btn btn-sm btn-ghost" onClick={() => pan(Math.round(viewSpanDays / 2))}>
                  {hiddenRight} milestone future →
                </button>
              )}
            </div>
          )}

          {/* Header mesi */}
          <div className="relative h-6 mb-2" style={{ marginLeft: 200 }}>
            <div className="relative h-full border-b border-line overflow-hidden">
              {months.map((m, i) => (
                <div key={i} className="absolute top-0 bottom-0 border-l border-line text-[12px] font-mono mute pl-1.5"
                  style={{ left: `${m.left}%` }}>
                  {m.label}
                </div>
              ))}
            </div>
          </div>

          {/* Barre */}
          <div className="relative space-y-2">
            {showToday && (
              // La riga di oggi. Il calcolo di prima moltiplicava una
              // percentuale per una lunghezza (`37% * (100% - 200px) / 100`):
              // in CSS non e' un'operazione valida, quindi tutta la calc()
              // cadeva e `left` tornava a `auto` — cioe' la riga di oggi stava
              // sempre incollata al bordo sinistro del Gantt, qualunque giorno
              // fosse. Una lunghezza per un numero puro, invece, si puo'.
              <div className="absolute top-0 bottom-0 w-px z-10 pointer-events-none"
                style={{ left: `calc(200px + (100% - 200px) * ${todayPct / 100})`, background: 'var(--accent)' }}>
                <div className="absolute -top-4 left-1 text-[12px] font-mono uppercase tracking-wide" style={{ color: 'var(--accent)' }}>Oggi</div>
              </div>
            )}

            {lanes.map((lane) => {
              const m = lane.milestone;
              // Clip al viewport
              const clippedStart = Math.max(0, lane.startPct);
              const clippedEnd = Math.min(100, lane.endPct);
              const widthPct = clippedEnd - clippedStart;
              const visible = widthPct > 0;
              const bg = m.done ? 'var(--ink)' : m.active ? 'var(--accent)' : 'var(--mute-2)';
              const clipLeft = lane.startPct < 0;
              const clipRight = lane.endPct > 100;
              return (
                <div key={m.id} className="grid items-center gap-2 cursor-pointer hover:bg-paper-2 rounded transition" style={{ gridTemplateColumns: '200px 1fr' }} title={`${m.label} — ${fmtDate(m.dueDate)}`}>
                  <div className="text-[13px] font-semibold flex items-center gap-2 px-2 py-1">
                    <div className="w-2 h-2 rounded-full flex-shrink-0" style={{ background: bg }} />
                    <span className="truncate">{m.label}</span>
                  </div>
                  <div className="relative h-7">
                    {visible ? (
                      <div className="absolute top-1.5 bottom-1.5 flex items-center px-2"
                        style={{
                          left: `${clippedStart}%`,
                          width: `${Math.max(2, widthPct)}%`,
                          background: bg,
                          opacity: m.done ? 0.4 : 1,
                          borderRadius: clipLeft && clipRight ? 0 : clipLeft ? '0 3px 3px 0' : clipRight ? '3px 0 0 3px' : 3,
                        }}>
                        <span className="text-[12px] font-mono text-white truncate">{fmtDateShort(m.dueDate)}</span>
                      </div>
                    ) : (
                      <div className="absolute top-1/2 -translate-y-1/2 text-[12px] mute-2 italic"
                        style={{ left: lane.endPct < 0 ? 4 : undefined, right: lane.startPct > 100 ? 4 : undefined }}>
                        {lane.endPct < 0 ? '◀ fuori vista' : 'fuori vista ▶'}
                      </div>
                    )}
                  </div>
                </div>
              );
            })}

            {!lanes.length && (
              <EmptyState
                icon="calendar"
                title="Nessuna milestone"
                description="Aggiungi la prima milestone per costruire il Gantt del progetto."
              />
            )}
          </div>
        </div>
      </div>
      </DesktopOnly>

      {/* "Prossime milestone": kicker-block nudo, niente card */}
      <div className="grid gap-6" style={{ gridTemplateColumns: '1fr 1fr' }}>
        <div className="ed-section">
          <div className="data-hd">
            <span className="t-micro">Prossime milestone</span>
          </div>
          <div>
            {p.milestones.filter((m) => !m.done).slice(0, 5).map((m) => {
              const dueMs = m.dueDate ? new Date(m.dueDate).getTime() : NaN;
              const days = Number.isFinite(dueMs) ? Math.ceil((dueMs - Date.now()) / 86400000) : null;
              return (
                <div key={m.id} className="flex items-center gap-3 py-2">
                  <div className="w-2 h-2 rounded-full" style={{ background: m.active ? 'var(--accent)' : 'var(--mute-2)' }} />
                  <div className="flex-1 min-w-0">
                    <div className=" font-medium truncate">{m.label}</div>
                    <div className="mute mono text-[12px]">{fmtDate(m.dueDate)}</div>
                  </div>
                  <Chip tone={days == null ? 'ghost' : days < 0 ? 'alert' : days < 7 ? 'warn' : 'ghost'}>
                    {days == null ? '—' : days < 0 ? `${Math.abs(days)} gg in ritardo` : `tra ${days} gg`}
                  </Chip>
                </div>
              );
            })}
            {/* Sul telefono, sotto l'avviso del Gantt, una sezione vuota senza
                una parola sembrava una pagina rotta. Solo telefono: il computer
                ha il Gantt sopra, che dice gia' che non ci sono milestone. */}
            {!p.milestones.some((m) => !m.done) && (
              <div className="m-only mute text-[13px] py-2">
                {p.milestones.length ? 'Tutte le milestone sono completate' : 'Nessuna milestone in questo progetto'}
              </div>
            )}
          </div>
        </div>

        {/* La colonna destra comincia con la sua intestazione, come la sinistra.
            Prima il bottone stava nudo in cima: le due colonne della stessa riga
            partivano a due altezze diverse, e il filo sopra c'era solo a
            sinistra. Adesso il comando sta dentro il kicker, che e' dove
            stanno i comandi di sezione in tutta la scheda. */}
        <div className="ed-section">
          <div className="data-hd">
            <span className="t-micro">Dipendenze critiche</span>
            <button className="btn btn-sm btn-ghost" aria-expanded={showDeps} onClick={() => setShowDeps((v) => !v)}>
              <Icon name={showDeps ? 'chevronD' : 'chevronR'} size={12} /> {showDeps ? 'Nascondi' : 'Mostra'}
            </button>
          </div>
          {showDeps && (
            <table className="tbl tbl--ed tbl--card pj-nolink">
              <thead><tr><th>Da</th><th>A</th><th>Tipo</th><th>Rischio</th></tr></thead>
              <tbody>
                {p.milestones.slice(0, -1).map((m, i) => {
                  const next = p.milestones[i + 1];
                  return (
                    <tr key={m.id}>
                      <td data-m="hd">{m.label}</td>
                      <td data-m="key" data-l="A">{next?.label ?? '—'}</td>
                      <td data-m="key" data-l="Tipo"><span className="mono mute text-[12px]">F→S</span></td>
                      <td data-m="key" data-l="Rischio"><Chip tone={m.active ? 'warn' : 'ok'}>{m.active ? 'in corso' : 'ok'}</Chip></td>
                    </tr>
                  );
                })}
                {p.milestones.length < 2 && <tr><td colSpan={4} className="text-center py-6 mute">Nessuna dipendenza</td></tr>}
              </tbody>
            </table>
          )}
        </div>
      </div>
    </div>
  );
}

// =====================================================================
// TASKS — filtri + toggle Kanban/Lista
// =====================================================================
function TasksTab({ p, quickAddReq }: { p: ProjectFull; quickAddReq: number }) {
  // Vista persistita: chi lavora per liste non deve rifare il toggle a ogni visita.
  const [viewSalvata, setViewState] = useState<'kanban' | 'list'>(() =>
    localStorage.getItem('project-tasks-view') === 'list' ? 'list' : 'kanban');
  // Sul telefono la bacheca non c'è (DesktopOnly qui sotto): si apriva la
  // scheda e al posto dei task c'era solo l'avviso, finché non si cambiava vista
  // dentro i Filtri. Sul telefono quindi è sempre la Lista, senza chiederlo.
  const isMobile = useIsMobile();
  const view = isMobile ? 'list' : viewSalvata;
  const setView = (v: 'kanban' | 'list') => {
    setViewState(v);
    localStorage.setItem('project-tasks-view', v);
  };
  const [filterAssignee, setFilterAssignee] = useState<string>('');
  const [filterPriority, setFilterPriority] = useState<string>('');
  const [filterTag, setFilterTag] = useState<string>('');
  const toggleDone = useToggleTaskDone();
  const qc = useQueryClient();

  const moveTask = useMutation({
    mutationFn: ({ id, status, position }: { id: string; status: string; position: number }) =>
      api.post(`/tasks/${id}/move`, { status, position }),
    // Optimistic update: rimuove la task dalla posizione vecchia e la inserisce nella nuova
    onMutate: async ({ id, status, position }) => {
      await qc.cancelQueries({ queryKey: ['project', p.id] });
      const prev = qc.getQueryData<any>(['project', p.id]);
      if (prev) {
        const moving = prev.tasks.find((t: any) => t.id === id);
        if (moving) {
          const others = prev.tasks.filter((t: any) => t.id !== id);
          const sameCol = others.filter((t: any) => t.status === status);
          const otherCols = others.filter((t: any) => t.status !== status);
          const updatedMoving = { ...moving, status };
          const safePos = Math.max(0, Math.min(position, sameCol.length));
          const newSameCol = [...sameCol.slice(0, safePos), updatedMoving, ...sameCol.slice(safePos)];
          qc.setQueryData(['project', p.id], {
            ...prev,
            tasks: [...otherCols, ...newSameCol],
          });
        }
      }
      return { prev };
    },
    onError: (_e, _v, ctx) => { if (ctx?.prev) qc.setQueryData(['project', p.id], ctx.prev); },
    onSettled: () => qc.invalidateQueries({ queryKey: ['project', p.id] }),
  });

  const dnd = useKanbanDnd<string>((taskId, newStatus, position) =>
    moveTask.mutate({ id: taskId, status: newStatus, position }),
  );

  // Quick-add inline: butta giù task al volo senza aprire il modale a 12 campi.
  const [quickCol, setQuickCol] = useState<string | null>(null);
  const [quickText, setQuickText] = useState('');
  const quickAdd = useMutation({
    mutationFn: ({ title, status }: { title: string; status: string }) =>
      api.post('/tasks', { projectId: p.id, title, status, priority: 'MEDIA' }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['project', p.id] }),
    onError: (e: any) => toast.alert('Task non creato', e?.message ?? 'Riprova'),
  });
  const submitQuick = (status: string) => {
    const title = quickText.trim();
    if (!title) { setQuickCol(null); return; }
    quickAdd.mutate({ title, status });
    setQuickText(''); // resta aperto: salva-e-aggiungi-un-altro
  };

  // Richiesta dal CTA header "Nuovo task": attiva il quick-add della colonna TODO
  // (l'input ha già autoFocus + Invio/Escape). In vista lista resetta solo il testo:
  // l'input quick-add lì è sempre visibile in fondo alla tabella.
  useEffect(() => {
    if (!quickAddReq) return;
    setQuickCol('TODO');
    setQuickText('');
  }, [quickAddReq]);

  const assignees = useMemo(() => {
    const map = new Map<string, { id: string; name: string; initials: string }>();
    for (const t of p.tasks) if (t.assignee) map.set(t.assignee.id ?? t.assignee.initials, { id: t.assignee.id ?? t.assignee.initials, name: `${t.assignee.firstName} ${t.assignee.lastName}`, initials: t.assignee.initials });
    return Array.from(map.values());
  }, [p.tasks]);

  const tags = useMemo(() => Array.from(new Set(p.tasks.map((t) => t.tag).filter(Boolean))) as string[], [p.tasks]);

  const filtered = p.tasks.filter((t) => {
    if (filterAssignee && t.assignee?.id !== filterAssignee && t.assignee?.initials !== filterAssignee) return false;
    if (filterPriority && t.priority !== filterPriority) return false;
    if (filterTag && t.tag !== filterTag) return false;
    return true;
  });

  const statuses = ['TODO', 'IN_PROGRESS', 'REVIEW', 'BLOCKED', 'DONE'];
  const statusLabels = TASK_STATUS_LABEL;
  const statusDot: Record<string, string> = {
    TODO: 'var(--mute)',
    IN_PROGRESS: 'var(--accent)',
    REVIEW: 'var(--warn)',
    BLOCKED: 'var(--alert)',
    DONE: 'var(--ok)',
  };

  const chiusi = filtered.filter((t) => t.status === 'DONE').length;
  const bloccati = filtered.filter((t) => t.status === 'BLOCKED').length;
  const quantiFiltri = [filterAssignee, filterPriority, filterTag].filter(Boolean).length;

  return (
    // space-y-4: 16px, lo stesso stacco che la barra filtri ha in tutte le
    // altre schermate (filterbar--float + mb-4). Erano 12.
    <div className="space-y-4">
      {/* Sul telefono i filtri stanno dietro un tasto solo: in fila erano una
          barra alta 106px prima ancora di vedere un task (mobile.md §5). */}
      <MobileFilters attivi={quantiFiltri}>
        <div className="filterbar filterbar--float mb-4 flex-wrap">
          <div className="tweak-seg d-only">
            <button aria-pressed={view === 'kanban'} onClick={() => setView('kanban')}><Icon name="kanban" size={12} /> Kanban</button>
            <button aria-pressed={view === 'list'} onClick={() => setView('list')}><Icon name="list" size={12} /> Lista</button>
          </div>
          <div className="divider d-only" />
          {/* 180, non 160: "Tutti gli assegnatari" non ci stava e usciva
              troncato a "Tutti gli assegnatar". inp-sm = --h-base 32px, come i
              menu della lista Progetti. */}
          <select className="inp inp-sm" style={{ width: 180 }} value={filterAssignee} onChange={(e) => setFilterAssignee(e.target.value)} aria-label="Assegnatario">
            <option value="">Tutti gli assegnatari</option>
            {assignees.map((a) => <option key={a.id} value={a.id}>{a.name}</option>)}
          </select>
          <select className="inp inp-sm" style={{ width: 140 }} value={filterPriority} onChange={(e) => setFilterPriority(e.target.value)} aria-label="Priorità">
            <option value="">Tutte le priorità</option>
            <option value="ALTA">Alta</option>
            <option value="MEDIA">Media</option>
            <option value="BASSA">Bassa</option>
          </select>
          {tags.length > 0 && (
            <select className="inp inp-sm" style={{ width: 140 }} value={filterTag} onChange={(e) => setFilterTag(e.target.value)} aria-label="Tag">
              <option value="">Tutti i tag</option>
              {tags.map((t) => <option key={t} value={t}>{t}</option>)}
            </select>
          )}
          <div className="spacer" />
          {/* "1 chiusi" era il singolare sbagliato che si nota. ("task" in
              italiano non si declina, resta com'e'.) */}
          <span className="mono mute text-[12px]">
            {filtered.length} task · {chiusi} {chiusi === 1 ? 'chiuso' : 'chiusi'} · {bloccati} {bloccati === 1 ? 'bloccato' : 'bloccati'}
          </span>
        </div>
      </MobileFilters>

      {view === 'kanban' ? (
        // Cinque colonne da guardare una accanto all'altra e schede che si
        // spostano trascinandole: su 390px non e' una vista. Si dice, e si
        // manda alla Lista, che dice le stesse cose (mobile.md §8). E' la
        // stessa scelta della bacheca in /progetti.
        <DesktopOnly
          cosa="La bacheca dei task"
          perche="sono colonne da guardare una accanto all'altra, e i task si spostano trascinandoli. Dal telefono resta la vista Lista, che dice le stesse cose."
        >
        {/* Niente gridAutoColumns scritto qui: la larghezza della colonna la
            decide .kanban (296px), ed e' la stessa della bacheca di /progetti.
            Erano 280 qui e 296 li', su due schermate a un clic di distanza. */}
        <div className="kanban">
          {statuses.map((s) => {
            const cards = filtered.filter((t) => t.status === s);
            const isOver = dnd.isOverColumn(s);
            const anyDragging = dnd.isAnyDragging();
            return (
              <div className={`kanban-col${isOver ? ' is-over' : ''}`} key={s} {...dnd.columnProps(s)}>
                <div className="kanban-col-hd">
                  <div className="kanban-col-title">
                    <span className="dot" style={{ background: statusDot[s] }} />
                    {statusLabels[s]}
                  </div>
                  <span className="kanban-col-count">{cards.length}</span>
                </div>
                <div className="kanban-col-body">
                  <DropZone dnd={dnd} status={s} position={0} visible={anyDragging} />
                  {cards.map((t, idx) => (
                    <Fragment key={t.id}>
                      <div className="kanban-card cursor-grab active:cursor-grabbing"
                        {...dnd.cardProps(t.id, t.status)}
                        onClick={() => openTask(t.id)}>
                        {t.status === 'BLOCKED' && t.blockedReason && (
                          <div className="flex items-start gap-1.5 mb-1.5 p-1.5 rounded text-[12px] bg-alert-bg text-alert">
                            <Icon name="alert" size={10} />
                            <span className="leading-tight">{t.blockedReason}</span>
                          </div>
                        )}
                        <div className="kanban-card-title">{t.title}</div>
                        <div className="flex gap-2 items-center mb-1.5 min-w-0">
                          <span className="inline-flex items-center gap-1.5 text-[12px] mute"><span className="w-1.5 h-1.5 rounded-full flex-shrink-0" style={{ background: t.priority === 'ALTA' ? 'var(--alert)' : t.priority === 'MEDIA' ? 'var(--warn)' : 'var(--mute-2)' }} />{t.priority.toLowerCase()}</span>
                          {/* Tag = etichetta informativa, non stato: niente pill */}
                          {t.tag && <span className="chip-label">{t.tag}</span>}
                        </div>
                        <div className="row between">
                          {t.assignee && (
                            <div className="row text-[12px]" style={{ gap: 4 }}>
                              <Avatar initials={t.assignee.initials} size={18} />
                              <span className="mute">{t.assignee.firstName[0]}. {t.assignee.lastName}</span>
                            </div>
                          )}
                          <span className="mono mute text-[12px]">{fmtDateShort(t.dueDate)}</span>
                        </div>
                        {t.estimateHours && Number(t.estimateHours) > 0 && (
                          <div className="mt-1.5 pt-1.5 border-t border-line text-[12px] mute mono flex justify-between">
                            <span>{Number(t.spentHours) || 0}h / {Number(t.estimateHours)}h</span>
                            <div className="barbar" style={{ width: 50 }}>
                              <span style={{ width: `${Math.min(100, ((Number(t.spentHours) || 0) / Number(t.estimateHours)) * 100)}%` }} />
                            </div>
                          </div>
                        )}
                      </div>
                      <DropZone dnd={dnd} status={s} position={idx + 1} visible={anyDragging} />
                    </Fragment>
                  ))}
                  {quickCol === s ? (
                    // .inp nudo: l'altezza e il corpo del testo li decide il
                    // token del campo, non tre utility scritte qui.
                    <input
                      className="inp"
                      autoFocus
                      placeholder="Titolo task, Invio per aggiungere…"
                      value={quickText}
                      onChange={(e) => setQuickText(e.target.value)}
                      onKeyDown={(e) => {
                        if (e.key === 'Enter') { e.preventDefault(); submitQuick(s); }
                        else if (e.key === 'Escape') { setQuickCol(null); setQuickText(''); }
                      }}
                      onBlur={() => { if (!quickText.trim()) setQuickCol(null); }}
                    />
                  ) : (
                    <button className="text-center py-2 border border-dashed border-line-2 rounded text-mute-2 text-[12px] hover:border-mute hover:text-mute w-full"
                      onClick={() => { setQuickCol(s); setQuickText(''); }}>
                      <Icon name="plus" size={10} /> aggiungi
                    </button>
                  )}
                </div>
              </div>
            );
          })}
        </div>
        </DesktopOnly>
      ) : (
        // Vista lista full-bleed: niente gabbia, conteggi già nella filterbar sopra
        <div className="ed-section">
          <table className="tbl tbl--ed tbl--card">
            <thead>
              <tr>
                <th style={{ width: 30 }} /><th>Titolo</th><th>Tag</th><th>Assegnato</th>
                <th>Stato</th><th>Priorità</th><th className="num">Ore</th><th className="num">Scadenza</th>
              </tr>
            </thead>
            <tbody>
              {filtered.map((t) => (
                <tr key={t.id} onClick={() => openTask(t.id)}>
                  {/* La spunta resta al computer: sul telefono la scheda si apre
                      toccandola, e il task si chiude da dentro. Un quadratino da
                      14px in una scheda sarebbe un bersaglio, non un comando. */}
                  <td data-m="off" onClick={(e) => { e.stopPropagation(); toggleDone.mutate({ id: t.id, done: t.status !== 'DONE' }); }}>
                    <div className="w-3.5 h-3.5 border border-line-2 rounded-sm grid place-items-center cursor-pointer hover:border-ink" style={{ background: t.status === 'DONE' ? 'var(--ink)' : 'var(--surface)' }}>
                      {t.status === 'DONE' && <Icon name="check" size={8} className="text-white" />}
                    </div>
                  </td>
                  <td data-m="hd" className={t.status === 'DONE' ? 'line-through text-mute' : 'font-medium'}>{t.title}</td>
                  <td data-m="off">{t.tag && <span className="chip-label">{t.tag}</span>}</td>
                  {/* Trattino invece della cella vuota: vedi la Panoramica. */}
                  <td data-m="key" data-l="Assegnato">{t.assignee
                    ? <div className="row" style={{ gap: 4 }}><Avatar initials={t.assignee.initials} size={20} />{t.assignee.firstName[0]}. {t.assignee.lastName}</div>
                    : <span className="mute-2">—</span>}</td>
                  <td data-m="key" data-l="Stato" style={{ whiteSpace: 'nowrap' }}><Chip tone={statusTone(t.status)}>{statusLabels[t.status]}</Chip></td>
                  <td data-m="key" data-l="Priorità"><span className="inline-flex items-center gap-1.5 text-[12px] mute"><span className="w-1.5 h-1.5 rounded-full" style={{ background: t.priority === 'ALTA' ? 'var(--alert)' : t.priority === 'MEDIA' ? 'var(--warn)' : 'var(--mute-2)' }} />{t.priority.toLowerCase()}</span></td>
                  <td data-m="off" className="num mono mute">{t.estimateHours ? `${Number(t.spentHours)} / ${Number(t.estimateHours)}` : '—'}</td>
                  <td data-m="key" data-l="Scadenza" className="num mono mute">{fmtDate(t.dueDate)}</td>
                </tr>
              ))}
              {/* Quick-add anche in lista: stessa submitQuick del kanban, entra in TODO */}
              {/* pj-addrow: non e' una riga di dati, e' il campo per
                  aggiungerne una. Niente chevron "si apre" sul telefono,
                  niente rail ink all'hover. */}
              <tr className="pj-addrow">
                <td data-m="off"><Icon name="plus" size={10} className="mute" /></td>
                <td data-m="row" colSpan={7}>
                  <input
                    className="inp w-full"
                    placeholder="Titolo task, Invio per aggiungere…"
                    value={quickText}
                    onChange={(e) => setQuickText(e.target.value)}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter') { e.preventDefault(); submitQuick('TODO'); }
                      else if (e.key === 'Escape') setQuickText('');
                    }}
                  />
                </td>
              </tr>
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

// =====================================================================
// DOCUMENTI — chip-filtro per cartella + ricerca
// =====================================================================
function DocsTab({ p }: { p: ProjectFull }) {
  const qc = useQueryClient();
  const token = useAuth((s) => s.accessToken);
  const [activeGroup, setActiveGroup] = useState<string | 'all'>('all');
  const [search, setSearch] = useState('');

  const groups = ['CONTRATTI', 'BRIEF', 'DESIGN', 'DELIVERY', 'ALTRO'];
  const groupLabels: Record<string, string> = { CONTRATTI: 'Contratti', BRIEF: 'Brief & Spec', DESIGN: 'Design', DELIVERY: 'Delivery', ALTRO: 'Altro' };
  const counts: Record<string, number> = {};
  for (const g of groups) counts[g] = p.documents.filter((d) => d.docGroup === g).length;

  const downloadDoc = async (docId: string, filename: string) => {
    const res = await fetch(`${BASE_API}/documents/${docId}/download`, {
      headers: token ? { Authorization: `Bearer ${token}` } : {},
      credentials: 'include',
    });
    if (!res.ok) { alert('Download fallito'); return; }
    const blob = await res.blob();
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url; a.download = filename;
    document.body.appendChild(a); a.click(); a.remove();
    URL.revokeObjectURL(url);
  };

  const deleteDoc = useMutation({
    mutationFn: (id: string) => api.delete(`/documents/${id}`),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['project', p.id] }),
  });

  const filtered = p.documents.filter((d) => {
    if (activeGroup !== 'all' && d.docGroup !== activeGroup) return false;
    if (search && !d.name.toLowerCase().includes(search.toLowerCase())) return false;
    return true;
  });

  const isMobile = useIsMobile();
  const ricerca = (
    <MobileFilters attivi={search.trim() ? 1 : 0} label="Cerca">
      <div className="filterbar filterbar--float mb-4 flex-wrap">
        <div className="tb-search" style={{ width: 240, margin: 0 }}>
          <Icon name="search" size={12} />
          <input aria-label="Cerca documenti" placeholder="Cerca documenti…" value={search} onChange={(e) => setSearch(e.target.value)} />
        </div>
      </div>
    </MobileFilters>
  );
  // Ex sidebar "Cartelle": stesse voci, ora chip-filtro con count sopra la tabella
  const cartelle = (
    <div className="flex gap-1.5 flex-wrap items-center">
      <Chip tone={activeGroup === 'all' ? 'active' : 'ghost'} onClick={() => setActiveGroup('all')}>
        Tutti <span className="mono">{p.documents.length}</span>
      </Chip>
      {groups.map((g) => (
        <Chip key={g} tone={activeGroup === g ? 'active' : 'ghost'} onClick={() => setActiveGroup(g)}>
          {groupLabels[g]} <span className="mono">{counts[g] ?? 0}</span>
        </Chip>
      ))}
    </div>
  );

  return (
    // space-y-4: 16px, lo stesso stacco della barra filtri nel resto dell'app.
    <div className="space-y-4">
      {/* Sul telefono la ricerca sta dietro un tasto solo (mobile.md §5).
          "Carica" e' uscito di qui: non e' un filtro, ed e' sceso nel kicker
          della sezione Documenti, che e' dove stanno i comandi di sezione in
          tutto il resto di questa scheda (Assegna risorsa, SAL manuale,
          Preventivo, Fattura). */}
      {isMobile ? (
        // Sul telefono cartelle e ricerca stanno sulla STESSA riga: cartelle a
        // sinistra in una striscia che scorre, "Cerca" in coda (mobile.md §4bis.4).
        // Prima "Cerca" stava da solo su una riga e le cartelle andavano a capo su due.
        <div className="pj-docs-bar">{cartelle}{ricerca}</div>
      ) : (
        <>
          {ricerca}
          {cartelle}
        </>
      )}

      {/* Tabella full-bleed: kicker + conteggio + la CTA a destra, come in tutte
          le altre sezioni di questa scheda */}
      <div className="ed-section">
        <div className="data-hd">
          <div className="flex items-center gap-2">
            <span className="t-micro">Documenti</span>
            <span className="mono mute text-[12px]">{filtered.length}</span>
          </div>
          <button className="btn btn-sm" onClick={() => useModalStore.getState().open('uploadDoc', { scope: 'project', scopeId: p.id })}>
            <Icon name="upload" size={12} /> Carica
          </button>
        </div>
        <table className="tbl tbl--ed tbl--card pj-nolink">
            <thead>
              <tr>
                {/* Niente colonna "Tipo": l'estensione è già nel nome del file */}
                <th>Nome</th><th>Versione</th><th>Autore</th><th>Data</th><th className="num">Dim.</th><th style={{ width: 60 }} />
              </tr>
            </thead>
            <tbody>
              {filtered.map((d) => {
                return (
                  <tr key={d.id}>
                    <td data-m="hd" className="font-medium">
                      <div className="flex items-center gap-1.5">
                        <Icon name="file" size={12} className="mute" />
                        {d.name}
                      </div>
                    </td>
                    <td data-m="key" data-l="Versione" className="mono mute">{d.version ?? 'v1'}</td>
                    <td data-m="key" data-l="Autore">{d.uploader ? <div className="row" style={{ gap: 4 }}><Avatar initials={d.uploader.initials} size={20} /><span className="text-[12px]">{d.uploader.firstName[0]}. {d.uploader.lastName}</span></div> : '—'}</td>
                    <td data-m="key" data-l="Data" className="mono mute">{fmtDate(d.createdAt)}</td>
                    <td data-m="key" data-l="Dimensione" className="num mono mute">{d.size ? `${(d.size / 1024).toFixed(0)} KB` : '—'}</td>
                    {/* Scarica ed elimina sono gesti rapidi: sul telefono si
                        prendono una riga tutta loro in fondo alla scheda. */}
                    <td data-m="row" data-l="Azioni">
                      <div className="row" style={{ gap: 2 }}>
                        <button
                          className="btn btn-sm btn-ghost"
                          title="Download"
                          onClick={() => downloadDoc(d.id, d.name)}
                        >
                          <Icon name="export" size={12} />
                        </button>
                        <button
                          className="btn btn-sm btn-ghost"
                          title="Elimina"
                          onClick={async () => {
                            if (await confirmDialog({
                              title: `Eliminare "${d.name}"?`,
                              danger: true,
                              confirmLabel: 'Elimina documento',
                            })) deleteDoc.mutate(d.id);
                          }}
                        >
                          <Icon name="x" size={12} />
                        </button>
                      </div>
                    </td>
                  </tr>
                );
              })}
              {!filtered.length && (
                <tr><td colSpan={6}>
                  <EmptyState
                    icon="file"
                    title="Nessun documento in questa cartella"
                    action={
                      <button className="btn btn-sm" onClick={() => useModalStore.getState().open('uploadDoc', { scope: 'project', scopeId: p.id })}><Icon name="upload" size={12} /> Carica il primo</button>
                    }
                  />
                </td></tr>
              )}
            </tbody>
        </table>
      </div>
    </div>
  );
}

// =====================================================================
// DIARIO — seg-control che monta Comunicazioni o Log attività (intatti)
// =====================================================================
function DiarioTab({ p, initialSection }: { p: ProjectFull; initialSection?: 'comms' | 'log' }) {
  const [section, setSection] = useState<'comms' | 'log'>(initialSection ?? 'comms');
  return (
    <div className="space-y-4">
      <div className="seg-control w-fit">
        <button aria-pressed={section === 'comms'} onClick={() => setSection('comms')}>Comunicazioni</button>
        <button aria-pressed={section === 'log'} onClick={() => setSection('log')}>Log attività</button>
      </div>
      {section === 'comms' ? <CommsTab p={p} /> : <ActivityTab p={p} />}
    </div>
  );
}

// =====================================================================
// COMUNICAZIONI — master/detail email-like
// =====================================================================
function CommsTab({ p }: { p: ProjectFull }) {
  const qc = useQueryClient();
  // Sul telefono elenco e messaggio non stanno affiancati: l'elenco prende
  // tutta la larghezza e il messaggio sale dal basso quando si tocca, come in
  // un'app di posta. Affiancati, a 390px, il testo usciva dallo schermo.
  const isMobile = useIsMobile();
  const [foglio, setFoglio] = useState(false);
  const [selected, setSelected] = useState<string | null>(p.communications[0]?.id ?? null);
  const [filter, setFilter] = useState<'all' | 'cliente' | 'interno'>('all');
  const [reply, setReply] = useState('');

  const sendReply = useMutation({
    mutationFn: (data: any) => api.post(`/projects/${p.id}/communications`, data),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['project', p.id] });
      setReply('');
      toast.ok('Risposta inviata');
    },
    onError: (e: any) => toast.alert('Invio fallito', e?.message ?? 'Riprova'),
  });

  const filtered = p.communications.filter((c) => {
    if (filter === 'cliente') return c.tag !== 'interno' && c.kind !== 'NOTE';
    if (filter === 'interno') return c.tag === 'interno' || c.kind === 'NOTE';
    return true;
  });
  const active = filtered.find((c) => c.id === selected) ?? filtered[0];

  const kindIcon = (k: string) => k === 'EMAIL' ? 'mail' : k === 'CALL' ? 'mail' : k === 'MEETING' ? 'calendar' : 'pin';

  return (
    // minmax(0, 1fr) sul telefono: con una colonna "auto" le righe troncate
    // (nowrap) allargavano la griglia fino a 3.140px.
    <div className="grid gap-6" style={{ gridTemplateColumns: isMobile ? 'minmax(0, 1fr)' : '380px 1fr' }}>
      {/* Lista nuda: kicker + conteggio, filtri e CTA nel kicker a destra */}
      <div className="ed-section flex flex-col" style={isMobile ? undefined : { maxHeight: 700 }}>
        <div className="data-hd">
          <div className="flex items-center gap-2">
            <span className="t-micro">Thread</span>
            <span className="mono mute text-[12px]">{filtered.length}</span>
          </div>
          <div className="flex gap-2 items-center">
            <div className="seg-control">
              <button aria-pressed={filter === 'all'} onClick={() => setFilter('all')}>Tutti</button>
              <button aria-pressed={filter === 'cliente'} onClick={() => setFilter('cliente')}>Cliente</button>
              <button aria-pressed={filter === 'interno'} onClick={() => setFilter('interno')}>Interno</button>
            </div>
            <button className="btn btn-sm" onClick={() => useModalStore.getState().open('newComm', { projectId: p.id })}>
              <Icon name="plus" size={12} />
            </button>
          </div>
        </div>
        <div className="flex-1 overflow-y-auto border-t border-line">
          {filtered.map((c) => (
            <div key={c.id}
              onClick={() => { setSelected(c.id); if (isMobile) setFoglio(true); }}
              className="px-4 py-3 border-b border-line cursor-pointer hover:bg-paper-2"
              style={{ background: active?.id === c.id ? 'var(--paper-2)' : undefined, borderLeft: active?.id === c.id ? '2px solid var(--accent)' : '2px solid transparent' }}>
              <div className="flex items-center gap-1.5 mb-1">
                <Icon name={kindIcon(c.kind) as any} size={12} className="mute" />
                <span className="font-mono text-[12px] uppercase mute tracking-wider">{c.kind}</span>
                <span className="spacer" />
                <span className="mono mute text-[12px]">{fmtDateShort(c.occurredAt)}</span>
              </div>
              <div className="font-semibold truncate">{c.subject}</div>
              {c.body && <div className="text-[12px] mute truncate mt-0.5">{c.body}</div>}
              {c.tag && <span className="chip-label mt-1">{c.tag}</span>}
            </div>
          ))}
          {!filtered.length && (
            <EmptyState
              icon="mail"
              title="Nessuna comunicazione"
              description="Registra email, call e meeting per tenere il diario del progetto."
            />
          )}
        </div>
      </div>

      {/* Detail: sul computer accanto all'elenco, sul telefono nel pannello dal basso. */}
      <DettaglioComunicazione mobile={isMobile} open={foglio && !!active} onClose={() => setFoglio(false)} titolo={active?.subject}>
      {active ? (
        <div className="card pj-comm">
          <div className="px-5 py-4 border-b border-line">
            <div className="flex items-center gap-2 mb-2">
              {/* Tipo comunicazione = etichetta, non stato */}
              <span className="chip-label">{active.kind.toLowerCase()}</span>
              {active.durationMin && <span className="mute text-[12px]">{active.durationMin} min</span>}
              {active.tag && <span className="mute text-[12px]">{active.tag}</span>}
              <span className="spacer" />
              <span className="mono mute text-[12px]">{fmtDate(active.occurredAt)}</span>
            </div>
            <h2 className="t-h3 leading-tight">{active.subject}</h2>
            <div className="flex items-center gap-2 mt-2 mute text-[13px]">
              {active.author && <><Avatar initials={active.author.initials} size={20} /> <span>{active.author.firstName} {active.author.lastName}</span></>}
              {active.toText && <><Icon name="arrowRight" size={10} /> <span>{active.toText}</span></>}
            </div>
          </div>
          <div className="px-5 py-4">
            {active.body ? (
              <div className=" leading-relaxed whitespace-pre-wrap">{active.body}</div>
            ) : (
              <div className="mute text-[13px] italic">Nessun corpo</div>
            )}
          </div>
          <div className="px-5 py-3 border-t border-line">
            <div className="flex items-center gap-2 mb-2">
              <span className="t-micro">Rispondi / aggiungi nota</span>
              <span className="spacer" />
              <span className="chip-label"><Icon name="check" size={10} /> SLA 24h</span>
            </div>
            <textarea className="inp" rows={3} placeholder="Scrivi una risposta…" value={reply} onChange={(e) => setReply(e.target.value)} />
            <div className="flex items-center gap-2 mt-2">
              <button
                className="btn btn-sm btn-ghost"
                onClick={() => useModalStore.getState().open('newTask', {
                  projectId: p.id,
                  prefill: { title: `Follow-up: ${active.subject}`, description: active.body ?? '' },
                })}
              >
                <Icon name="plus" size={10} /> Crea task da qui
              </button>
              <span className="spacer" />
              <button
                className="btn btn-sm"
                disabled={!reply.trim() || sendReply.isPending}
                onClick={() => sendReply.mutate({
                  kind: 'NOTE',
                  subject: `Re: ${active.subject}`,
                  body: reply,
                  occurredAt: new Date().toISOString(),
                  tag: active.tag,
                })}
              >
                <Icon name="send" size={12} /> {sendReply.isPending ? 'Invio…' : 'Invia'}
              </button>
            </div>
          </div>
        </div>
      ) : (
        <div className="grid place-items-center" style={{ minHeight: 400 }}>
          <EmptyState icon="mail" title="Seleziona una comunicazione" />
        </div>
      )}
      </DettaglioComunicazione>
    </div>
  );
}

/** Sul computer restituisce il dettaglio così com'è (stesso DOM di prima); sul telefono lo mette nel pannello dal basso. */
function DettaglioComunicazione({ mobile, open, onClose, titolo, children }: {
  mobile: boolean; open: boolean; onClose: () => void; titolo?: string; children: ReactNode;
}) {
  if (!mobile) return <>{children}</>;
  return <MobileSheet open={open} onClose={onClose} title={titolo}>{children}</MobileSheet>;
}

// =====================================================================
// GESTIONE — Team & Ore + Economics fuse in un'unica tab: prima le
// risorse, poi budget/fatture. La sezione Marginalità dentro Economics
// resta gated su canSeeMargin (vedi EconomicsTab), il resto è per tutti.
// =====================================================================
function GestioneTab({ p, showMargin }: { p: ProjectFull; showMargin: boolean }) {
  return (
    <div className="space-y-6">
      {/* Sub-header di sezione: stessa grammatica .data-hd/kicker della Dashboard */}
      <div id="gestione-team" className="data-hd">
        <span className="t-micro">Team & Ore · Risorse</span>
      </div>
      <TeamTab p={p} showMargin={showMargin} />
      {/* Separatore tra le due sezioni della tab */}
      <div className="border-t border-line pt-6">
        <div className="data-hd">
          <span className="t-micro">Economics · Budget & fatture</span>
        </div>
      </div>
      <EconomicsTab p={p} showMargin={showMargin} />
      {/* Sul computer non disegna niente (i figli sono vuoti); sul telefono è la
          riga onesta sui comandi rimasti al computer (mobile.md §8). Prima stava
          in cima a ogni scheda del progetto. */}
      <DesktopOnly
        cosa="Creare un preventivo o eliminare il progetto"
        perche="sono una procedura a passi e una verifica che si fanno meglio sullo schermo grande."
      ><></></DesktopOnly>
    </div>
  );
}

// =====================================================================
// TEAM & ORE — KPI + tabella avanzamento + timesheet heatmap
// =====================================================================
function TeamTab({ p, showMargin }: { p: ProjectFull; showMargin: boolean }) {
  const [weekStart, setWeekStart] = useState<string>(() => {
    const d = new Date();
    const day = d.getDay();
    const diff = d.getDate() - day + (day === 0 ? -6 : 1);
    return new Date(d.setDate(diff)).toISOString().slice(0, 10);
  });

  const shiftWeek = (deltaDays: number) => {
    const d = new Date(weekStart);
    d.setDate(d.getDate() + deltaDays);
    setWeekStart(d.toISOString().slice(0, 10));
  };
  const resetWeek = () => {
    const d = new Date();
    const day = d.getDay();
    const diff = d.getDate() - day + (day === 0 ? -6 : 1);
    setWeekStart(new Date(d.setDate(diff)).toISOString().slice(0, 10));
  };

  const { data: teamProgress } = useQuery({
    queryKey: ['project-team-progress', p.id],
    queryFn: () => api.get<Array<{ id: string; role: string; isInternal: boolean; user: any; supplier: any; hourlyRate?: number; hoursPlanned: number; spentHours: number; pct: number; cost?: number }>>(`/projects/${p.id}/team-progress`),
  });
  const { data: timesheet } = useQuery({
    queryKey: ['project-timesheet', p.id, weekStart],
    queryFn: () => api.get<{ weekStart: string; rows: Array<{ user: any; days: number[]; total: number }> }>(`/projects/${p.id}/timesheet?week=${weekStart}`),
  });

  const totalPlanned = (teamProgress ?? []).reduce((a, r) => a + (Number(r.hoursPlanned) || 0), 0);
  const totalSpent = (teamProgress ?? []).reduce((a, r) => a + (Number(r.spentHours) || 0), 0);
  const totalCost = (teamProgress ?? []).reduce((a, r) => a + (Number(r.cost) || 0), 0);
  const avgPct = totalPlanned > 0 ? Math.round((totalSpent / totalPlanned) * 100) : 0;

  // Heatmap a singolo accento: rampa viola Masamune (mono+accent, niente blu).
  const heatColor = (h: number) => {
    if (h === 0) return 'var(--paper-2)';
    if (h <= 2) return 'color-mix(in oklab, var(--accent) 18%, var(--surface))';
    if (h <= 5) return 'color-mix(in oklab, var(--accent) 42%, var(--surface))';
    if (h <= 8) return 'color-mix(in oklab, var(--accent) 70%, var(--surface))';
    return 'var(--accent)';
  };

  return (
    <div className="space-y-6">
      {/* Numeri NUDI sul foglio: stat-band al posto della griglia KPI */}
      <div className="stat-band">
        <div>
          <div className="st-label">Ore pianificate</div>
          <div className="st-value"><AnimatedNumber value={totalPlanned} /><span className="u">h</span></div>
        </div>
        <div>
          <div className="st-label">Ore consuntivate</div>
          <div className="st-value"><AnimatedNumber value={Math.round(totalSpent)} /><span className="u">h</span></div>
          <div className="st-delta">{avgPct}% del piano</div>
        </div>
        <div>
          <div className="st-label">Risorse allocate</div>
          <div className="st-value"><AnimatedNumber value={teamProgress?.length ?? 0} /></div>
          <div className="st-delta">{(teamProgress ?? []).filter((r) => r.isInternal).length} interne</div>
        </div>
        {showMargin ? (
          <div>
            <div className="st-label">Costo team</div>
            <div className="st-value"><span className="u">€</span><AnimatedNumber value={totalCost} format={fmtEur} /></div>
          </div>
        ) : (
          // Gating canSeeMargin: il numero resta sottovoce
          <div>
            <div className="st-label">Costo team</div>
            <div className="st-value mute-2">•••</div>
            <div className="st-delta">Riservato</div>
          </div>
        )}
      </div>

      {/* Tabella full-bleed: kicker + conteggio + stessa CTA a destra */}
      <div className="ed-section">
        <div className="data-hd">
          <div className="flex items-center gap-2">
            <span className="t-micro">Allocazioni team</span>
            <span className="mono mute text-[12px]">{teamProgress?.length ?? 0}</span>
          </div>
          <button className="btn btn-sm" onClick={() => useModalStore.getState().open('assignMember', { projectId: p.id })}>
            <Icon name="plus" size={12} /> Assegna risorsa
          </button>
        </div>
        <table className="tbl tbl--ed tbl--card pj-nolink">
          <thead>
            <tr>
              <th>Persona</th><th>Ruolo</th><th>Tipo</th>
              <th className="num">Ore pianif</th><th className="num">Ore spese</th><th>Avanzamento</th>
              {showMargin && <th className="num">Tariffa</th>}
              {showMargin && <th className="num">Costo</th>}
            </tr>
          </thead>
          <tbody>
            {(teamProgress ?? []).map((r) => (
              <tr key={r.id}>
                <td data-m="hd">
                  <div className="row">
                    <Avatar initials={r.user?.initials ?? r.supplier?.fullName?.slice(0, 2).toUpperCase() ?? '??'} size={22} />
                    <span className="font-semibold">{r.user ? `${r.user.firstName} ${r.user.lastName}` : r.supplier?.fullName}</span>
                  </div>
                </td>
                <td data-m="key" data-l="Ruolo">{r.role}</td>
                {/* Interno/Esterno = etichetta informativa, non stato */}
                <td data-m="key" data-l="Tipo"><span className="chip-label">{r.isInternal ? 'Interno' : 'Esterno'}</span></td>
                <td data-m="key" data-l="Ore pianificate" className="num mono">{r.hoursPlanned} h</td>
                <td data-m="key" data-l="Ore spese" className="num mono">{r.spentHours.toFixed(1)} h</td>
                <td data-m="row" data-l="Avanzamento">
                  <div className="row" style={{ gap: 6 }}>
                    <div className="barbar" style={{ width: 80 }}>
                      <span style={{
                        width: `${Math.min(100, r.pct)}%`,
                        background: r.pct > 100 ? 'var(--alert)' : 'var(--accent)',
                      }} />
                    </div>
                    <span className="mono mute text-[12px]">{r.pct}%</span>
                  </div>
                </td>
                {showMargin && <td data-m="off" className="num mono">€ {r.hourlyRate}/h</td>}
                {showMargin && <td data-m="key" data-l="Costo" className="num mono">€ {fmtEur(r.cost ?? 0)}</td>}
              </tr>
            ))}
            {!teamProgress?.length && (
              <tr><td colSpan={showMargin ? 8 : 6}>
                <EmptyState
                  icon="users"
                  title="Nessuna allocazione"
                  description="Assegna la prima risorsa per pianificare ore e costi."
                />
              </td></tr>
            )}
          </tbody>
        </table>
      </div>

      <div className="grid gap-6" style={{ gridTemplateColumns: '2fr 1fr' }}>
        {/* Heatmap nuda: kicker + settimana, navigazione invariata a destra.
            Sette giorni in colonna piu' il nome e il totale: nove colonne che
            su 390px diventano quadratini da 30px. E' una griglia da leggere di
            traverso, quindi resta al computer (mobile.md §8). */}
        <DesktopOnly
          cosa="La griglia delle ore della settimana"
          perche="sono sette giorni affiancati per persona, e su uno schermo stretto le caselle diventano illeggibili. Le ore totali stanno nella fascia dei numeri qui sopra."
        >
        <div className="ed-section">
          <div className="data-hd">
            <div className="flex items-center gap-2">
              <span className="t-micro">Timesheet settimana</span>
              <span className="mono mute text-[12px]">{fmtDate(timesheet?.weekStart ?? weekStart)}</span>
            </div>
            <div className="seg-control">
              <button onClick={() => shiftWeek(-7)}>← Prec</button>
              <button onClick={resetWeek}>Oggi</button>
              <button onClick={() => shiftWeek(7)}>Succ →</button>
            </div>
          </div>
          <div>
            <table className="w-full text-[13px]">
              <thead>
                {/* Micro-etichette standard (.t-micro), niente stili uppercase fatti a mano */}
                <tr>
                  <th className="t-micro text-left pb-2">Persona</th>
                  {['Lun', 'Mar', 'Mer', 'Gio', 'Ven', 'Sab', 'Dom'].map((d) => (
                    <th key={d} className="t-micro text-center pb-2">{d}</th>
                  ))}
                  <th className="t-micro text-right pb-2">Tot</th>
                </tr>
              </thead>
              <tbody>
                {(timesheet?.rows ?? []).map((row, i) => (
                  <tr key={i}>
                    <td className="py-1">
                      <div className="row"><Avatar initials={row.user.initials} size={20} /><span className="text-[13px]">{row.user.firstName[0]}. {row.user.lastName}</span></div>
                    </td>
                    {row.days.map((h, di) => (
                      <td key={di} className="text-center py-1">
                        <div className="inline-block w-8 h-8 rounded grid place-items-center mono text-[12px]"
                          style={{ background: heatColor(h), color: h > 5 ? 'white' : 'var(--ink)' }}>
                          {h > 0 ? h.toFixed(0) : ''}
                        </div>
                      </td>
                    ))}
                    <td className="text-right mono font-semibold">{row.total.toFixed(1)}h</td>
                  </tr>
                ))}
                {!timesheet?.rows.length && (
                  <tr><td colSpan={9}>
                    <EmptyState icon="calendar" title="Nessuna registrazione" description="In questa settimana non risultano ore consuntivate." />
                  </td></tr>
                )}
              </tbody>
            </table>
          </div>
        </div>
        </DesktopOnly>

        {/* Kicker-block senza bordi nella colonna laterale */}
        <div className="ed-section">
          <div className="data-hd">
            <span className="t-micro">Carico vs capacità</span>
          </div>
          <div className="space-y-2">
            {(teamProgress ?? []).slice(0, 6).map((r) => {
              const cap = 160; // capacità mensile standard
              const pct = Math.round(((Number(r.hoursPlanned) || 0) / cap) * 100);
              return (
                <div key={r.id}>
                  <div className="flex items-center justify-between mb-1.5">
                    <div className="row" style={{ gap: 6 }}>
                      <Avatar initials={r.user?.initials ?? 'XX'} size={18} />
                      <span className=" font-medium">{r.user?.firstName?.[0]}. {r.user?.lastName ?? r.supplier?.fullName}</span>
                    </div>
                    <span className={`mono text-[12px] ${pct > 100 ? 'text-alert font-semibold' : pct > 85 ? 'text-warn' : 'mute'}`}>{pct}%</span>
                  </div>
                  <div className="barbar">
                    <span style={{
                      width: `${Math.min(100, pct)}%`,
                      background: pct > 100 ? 'var(--alert)' : pct > 85 ? 'var(--warn)' : 'var(--accent)',
                    }} />
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </div>
    </div>
  );
}

// =====================================================================
// ECONOMICS — Budget & Fatture (per tutti) + sezione Marginalità (solo
// canSeeMargin), ora sezione della tab Gestione.
// =====================================================================
function EconomicsTab({ p, showMargin }: { p: ProjectFull; showMargin: boolean }) {
  return (
    <div className="space-y-6">
      <BudgetTab p={p} />
      {/* Team & Ore vive nella stessa tab, più in alto: si scrolla all'ancora */}
      {showMargin && <MarginSection p={p} onGoTeam={() => document.getElementById('gestione-team')?.scrollIntoView({ behavior: 'smooth', block: 'start' })} />}
    </div>
  );
}

// =====================================================================
// BUDGET — KPI cash-in + barra stacked
// =====================================================================
function BudgetTab({ p }: { p: ProjectFull }) {
  const openModal = useModalStore((s) => s.open);
  const { data: cf } = useQuery({
    queryKey: ['project-cash-flow', p.id],
    queryFn: () => api.get<{ contractValue: number; collected: number; issued: number; forecast: number; approvedChangeRequests: number }>(`/projects/${p.id}/cash-flow`),
  });

  const contract = cf?.contractValue ?? p.revenue;
  const collected = cf?.collected ?? 0;
  const issued = cf?.issued ?? 0;
  const forecast = cf?.forecast ?? 0;
  const collectedPct = contract > 0 ? (collected / contract) * 100 : 0;
  const issuedPct = contract > 0 ? (issued / contract) * 100 : 0;
  const forecastPct = contract > 0 ? (forecast / contract) * 100 : 0;

  // Residuo da fatturare = revenue − fatturato, calcolato dalle fatture GIÀ nel
  // payload (ANNULLATE escluse). Se le fatture non ci sono, niente bottone: zero fetch nuovi.
  const fatturato = p.invoices
    ? p.invoices.filter((i) => i.status !== 'ANNULLATA').reduce((a, i) => a + (Number(i.total) || 0), 0)
    : null;
  const residuo = fatturato != null ? p.revenue - fatturato : 0;

  return (
    <div className="space-y-6">
      {/* Cash-in in numeri NUDI: stat-band al posto della griglia KPI */}
      <div className="stat-band">
        <div>
          <div className="st-label">Valore contratto</div>
          <div className="st-value"><span className="u">€</span><AnimatedNumber value={contract} format={fmtEur} /></div>
          {/* Dati ordinari: niente semaforo verde, delta neutro */}
          {cf && cf.approvedChangeRequests > 0 && <div className="st-delta">+€ {fmtEur(cf.approvedChangeRequests)} CR</div>}
        </div>
        <div>
          <div className="st-label">Incassato</div>
          <div className="st-value"><span className="u">€</span><AnimatedNumber value={collected} format={fmtEur} /></div>
          <div className="st-delta">{collectedPct.toFixed(0)}%</div>
        </div>
        <div>
          <div className="st-label">Emesso in incasso</div>
          <div className="st-value"><span className="u">€</span><AnimatedNumber value={issued} format={fmtEur} /></div>
          <div className="st-delta">{issuedPct.toFixed(0)}%</div>
        </div>
        <div>
          <div className="st-label">Da emettere</div>
          <div className="st-value"><span className="u">€</span><AnimatedNumber value={forecast} format={fmtEur} /></div>
          <div className="st-delta">{forecastPct.toFixed(0)}%</div>
        </div>
      </div>

      {/* Barra stacked nuda: kicker + stessa CTA a destra */}
      <div className="ed-section">
        <div className="data-hd">
          <span className="t-micro">Avanzamento cash-in</span>
          {fatturato != null && residuo > 0 && (
            // One-click: fattura pre-compilata col residuo non ancora fatturato
            <button
              className="btn btn-sm"
              title={`Crea una fattura da € ${fmtEur(residuo)}`}
              onClick={() => openModal('newInvoice', { clientId: p.client.id, projectId: p.id, amount: residuo })}
            >
              <Icon name="plus" size={12} /> Fattura residuo
            </button>
          )}
        </div>
        <div>
          <div className="h-7 rounded overflow-hidden flex bg-paper-2">
            <div style={{ width: `${collectedPct}%`, background: 'var(--ok)' }} title={`Incassato € ${fmtEur(collected)}`} />
            <div style={{ width: `${issuedPct}%`, background: 'var(--accent)' }} title={`Emesso € ${fmtEur(issued)}`} />
            <div style={{ width: `${forecastPct}%`, background: 'var(--warn)', opacity: 0.6 }} title={`Previsto € ${fmtEur(forecast)}`} />
          </div>
          <div className="flex gap-4 mt-3 text-[12px]">
            <span className="flex items-center gap-1.5"><span className="w-2 h-2 rounded-full bg-ok" /> Incassato € {fmtEur(collected)} ({collectedPct.toFixed(0)}%)</span>
            <span className="flex items-center gap-1.5"><span className="w-2 h-2 rounded-full" style={{ background: 'var(--accent)' }} /> Emesso € {fmtEur(issued)} ({issuedPct.toFixed(0)}%)</span>
            <span className="flex items-center gap-1.5"><span className="w-2 h-2 rounded-full bg-warn opacity-60" /> Previsto € {fmtEur(forecast)} ({forecastPct.toFixed(0)}%)</span>
          </div>
        </div>
      </div>

      {/* Tabella full-bleed: kicker + conteggio + stessa CTA a destra */}
      <div className="ed-section">
        <div className="data-hd">
          <div className="flex items-center gap-2">
            <span className="t-micro">Piano SAL</span>
            <span className="mono mute text-[12px]">{p.salPayments.length}</span>
          </div>
          <button className="btn btn-sm" onClick={() => openModal('newSAL', { projectId: p.id })}>
            <Icon name="plus" size={12} /> SAL manuale
          </button>
        </div>
        <table className="tbl tbl--ed tbl--card pj-nolink">
          <thead><tr><th>Milestone</th><th className="num">Importo</th><th>Scadenza</th><th>Stato</th></tr></thead>
          <tbody>
            {p.salPayments.map((s) => (
              <tr key={s.id}>
                <td data-m="hd">{s.label}</td>
                <td data-m="key" data-l="Importo" className="num">€ {fmtEur(s.amount)}</td>
                <td data-m="key" data-l="Scadenza" className="mono mute">{fmtDate(s.dueDate)}</td>
                <td data-m="key" data-l="Stato"><Chip tone={statusTone(s.status)}>{s.status}</Chip></td>
              </tr>
            ))}
            {/* is-total invece dello sfondo scritto nello style: cosi' la riga
                smette anche di accendersi all'hover come se si potesse aprire. */}
            <tr className="is-total">
              <td data-m="hd">Totale piano</td>
              <td data-m="key" className="num">€ {fmtEur((p.salPayments ?? []).reduce((a, s) => a + (Number(s.amount) || 0), 0))}</td>
              <td data-m="off" colSpan={2} />
            </tr>
          </tbody>
        </table>
      </div>

      {p.changeRequests.length > 0 && (
        <div className="ed-section">
          <div className="data-hd">
            <div className="flex items-center gap-2">
              <span className="t-micro">Change requests</span>
              <span className="mono mute text-[12px]">{p.changeRequests.length}</span>
            </div>
          </div>
          <table className="tbl tbl--ed tbl--card pj-nolink">
            <thead><tr><th>Richiesta</th><th className="num">Importo</th><th>Data</th><th>Stato</th></tr></thead>
            <tbody>
              {p.changeRequests.map((c) => (
                <tr key={c.id}>
                  <td data-m="hd">{c.label}</td>
                  <td data-m="key" data-l="Importo" className="num">€ {fmtEur(c.amount)}</td>
                  <td data-m="key" data-l="Data" className="mono mute">{fmtDate(c.date)}</td>
                  <td data-m="key" data-l="Stato"><Chip tone={statusTone(c.status)}>{c.status.replace(/_/g, ' ')}</Chip></td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      <ProjectDocumentsCard p={p} />
    </div>
  );
}

// =====================================================================
// Documenti commerciali del progetto: preventivi + fatture in un'unica card
// con CTA per crearne di nuovi pre-popolati col contesto del progetto.
// =====================================================================
function ProjectDocumentsCard({ p }: { p: ProjectFull }) {
  const openModal = useModalStore((s) => s.open);
  const navigate = useNavigate();

  const { data: quotesData } = useQuery({
    queryKey: ['quotes', 'project', p.id],
    queryFn: () => api.get<{ items: Array<{ id: string; number: string | null; title: string | null; status: string; total: string; issueDate: string | null; signedAt: string | null; convertedToInvoice: { id: string; number: string | null } | null }> }>(`/quotes?projectId=${p.id}&pageSize=50`),
  });
  const quotes = quotesData?.items ?? [];

  const hasAny = quotes.length > 0 || p.invoices.length > 0;
  const newQuote = () => openModal('newQuote', { clientId: p.client.id, projectId: p.id });
  const newInvoice = () => openModal('newInvoice', { clientId: p.client.id, projectId: p.id });

  return (
    // Sezione full-bleed: kicker + conteggi mono + le stesse CTA a destra
    <div className="ed-section">
      <div className="data-hd">
        <div className="flex items-center gap-2">
          <span className="t-micro">Documenti commerciali</span>
          <span className="mono mute text-[12px]">{quotes.length} preventivi · {p.invoices.length} fatture</span>
        </div>
        <div className="flex gap-1.5">
          <button className="btn btn-sm" onClick={newQuote}>
            <Icon name="plus" size={10} /> Preventivo
          </button>
          <button className="btn btn-sm" onClick={newInvoice}>
            <Icon name="plus" size={10} /> Fattura
          </button>
        </div>
      </div>

      {!hasAny ? (
        <EmptyState
          icon="file"
          title="Ancora nessun documento"
          description={<>Il flusso consigliato è: <strong>preventivo firmato</strong> → conversione automatica in fattura. Cominciare dal preventivo ti aiuta a tracciare la conversion-rate.</>}
          action={
            <button className="btn" onClick={newQuote}>
              <Icon name="file" size={12} /> Crea il primo preventivo
            </button>
          }
        />
      ) : (
        <>
          {quotes.length > 0 && (
            <>
              <div className="t-micro mb-2">Preventivi</div>
              <table className="tbl tbl--ed tbl--card">
                <thead><tr><th>N°</th><th>Titolo</th><th>Data</th><th>Stato</th><th className="num">Totale</th><th /></tr></thead>
                <tbody>
                  {quotes.map((q) => (
                    <tr key={q.id} className="cursor-pointer hover:bg-paper-2" onClick={() => navigate(`/quotes/${q.id}`)}>
                      <td data-m="key" data-l="Numero" className="mono">{q.number ?? '—'}</td>
                      <td data-m="hd">{q.title ?? 'Proposta'}</td>
                      <td data-m="key" data-l="Data" className="mono mute">{fmtDate(q.issueDate)}</td>
                      <td data-m="key" data-l="Stato">
                        <Chip tone={
                          q.status === 'ACCETTATO' || q.status === 'CONVERTITO' ? 'ok'
                          : q.status === 'INVIATO' || q.status === 'VISUALIZZATO' ? 'warn'
                          : q.status === 'RIFIUTATO' || q.status === 'SCADUTO' ? 'alert'
                          : 'info'
                        } dot>{q.status}</Chip>
                      </td>
                      <td data-m="key" data-l="Totale" className="num mono font-semibold">€ {fmtEur(q.total)}</td>
                      {/* La freccia dice "si apre": sul telefono lo dice gia' il
                          chevron che .tbl--card mette sulla scheda. */}
                      <td data-m="off"><Icon name="arrowRight" size={10} className="mute" /></td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </>
          )}

          {p.invoices.length > 0 && (
            <>
              {/* I due gruppi impilati si separano con una hairline, non col grigio */}
              {quotes.length > 0 && <div className="ed-divider" />}
              <div className="t-micro mb-2">Fatture</div>
              <table className="tbl tbl--ed tbl--card">
                <thead><tr><th>N°</th><th>Emissione</th><th>Stato</th><th className="num">Totale</th><th /></tr></thead>
                <tbody>
                  {p.invoices.map((i) => (
                    <tr key={i.id} className="cursor-pointer hover:bg-paper-2" onClick={() => navigate(`/invoices/${i.id}`)}>
                      <td data-m="hd" className="mono">{i.number ?? 'BOZZA'}</td>
                      <td data-m="key" data-l="Emissione" className="mono mute">{fmtDate(i.issueDate)}</td>
                      <td data-m="key" data-l="Stato"><Chip tone={statusTone(i.status)} dot>{i.status}</Chip></td>
                      <td data-m="key" data-l="Totale" className="num mono font-semibold">€ {fmtEur(i.total)}</td>
                      <td data-m="off"><Icon name="arrowRight" size={10} className="mute" /></td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </>
          )}
        </>
      )}
    </div>
  );
}

// =====================================================================
// MARGINALITÀ — sezione di Economics: breakdown + forecast 5 scenari.
// Il dettaglio del costo team vive in Team & Ore (qui solo il totale).
// =====================================================================
function MarginSection({ p, onGoTeam }: { p: ProjectFull; onGoTeam: () => void }) {
  const cost = p.cost ?? 0;
  const { abs, pct } = calcMargin(p.revenue, cost);
  const tone = pct < 20 ? 'alert' : pct < 30 ? 'warn' : 'ok';

  const { data: breakdown } = useQuery({
    queryKey: ['project-cost-breakdown', p.id],
    queryFn: () => api.get<{ total: number; items: Array<{ category: string; amount: number; pct: number }> }>(`/projects/${p.id}/cost-breakdown`),
  });
  const { data: teamProgress } = useQuery({
    queryKey: ['project-team-progress', p.id],
    queryFn: () => api.get<Array<{ id: string; user: any; supplier: any; role: string; hourlyRate?: number; spentHours: number; cost?: number }>>(`/projects/${p.id}/team-progress`),
  });

  // Forecast 5 scenari
  const crApproved = (p.changeRequests ?? []).filter((c) => c.status === 'APPROVATA').reduce((a, c) => a + (Number(c.amount) || 0), 0);
  const scenarios = [
    { name: 'Baseline', revenue: p.revenue, cost, note: 'Margine attuale' },
    { name: 'Con CR approvate', revenue: p.revenue + crApproved, cost, note: '+€ ' + fmtEur(crApproved) + ' revenue' },
    { name: 'Ottimista', revenue: p.revenue, cost: cost * 0.9, note: '-10% costi' },
    { name: 'Burn attuale', revenue: p.revenue, cost: cost * 1.1, note: '+10% costi' },
    { name: 'Pessimista', revenue: p.revenue, cost: cost * 1.25, note: '+25% costi' },
  ].map((s) => ({ ...s, ...calcMargin(s.revenue, s.cost) }));

  const categoryLabels: Record<string, string> = {
    SVILUPPO: 'Sviluppo',
    DESIGN: 'Design',
    PM: 'Project Management',
    PROFESSIONISTI: 'Professionisti',
    QA: 'QA / Tooling',
    INFRASTRUTTURA: 'Infrastruttura',
    TOOLS: 'Tools',
    DOCENTI: 'Docenti',
    RECRUITER: 'Recruiter',
    ADV: 'ADV',
    FORN: 'Fornitori',
    ALTRO: 'Altro',
  };

  return (
    <div className="space-y-6">
      {/* Header gated: keyline viola + lock = "qui i numeri si guardano sottovoce" */}
      <div className="p-3 rounded border border-line bg-paper-2 flex items-center gap-3 text-[13px]">
        <span className="riservato-keyline">
          <Icon name="lock" size={12} />
          <span className="t-micro">Riservato</span>
        </span>
        <span><b>Solo Super Admin</b> — ogni accesso a questi dati viene loggato nel registro audit.</span>
      </div>

      {/* stat-band: numeri nudi sul foglio, come le altre tre fasce di questa
          scheda (Panoramica, Team & Ore, Budget). Qui era rimasta l'unica
          griglia KPI incorniciata: quattro scatole con il bordo, in mezzo a una
          pagina che i numeri li scrive senza gabbia. Stessi dati, stessa
          gerarchia, stesso posto: cambia solo che adesso e' d'accordo con le
          altre schermate. */}
      <div className="stat-band">
        <div>
          <div className="st-label">Ricavi</div>
          <div className="st-value"><span className="u">€</span>{fmtEur(p.revenue)}</div>
        </div>
        <div>
          <div className="st-label">Costi totali</div>
          <div className="st-value"><span className="u">€</span>{fmtEur(cost)}</div>
        </div>
        <div>
          <div className="st-label">Margine lordo</div>
          {/* Senza costi non c'e' nessun margine da scrivere: il conto darebbe
              "100% sui ricavi", che si legge come un progetto perfetto e invece
              vuol dire solo che i costi non li ha ancora messi nessuno. E' la
              stessa cosa che gia' dicono il pannello Marginalita' in Panoramica
              ("Non si sa ancora") e la colonna Valore nella lista ("costi da
              inserire"): qui invece la stessa pagina scriveva € 4.800 e 100%.
              Margine in viola; il semaforo resta solo per soglia violata. */}
          {cost === 0 ? (
            <>
              <div className="st-value mute-2">—</div>
              <div className="st-delta">costi da inserire</div>
            </>
          ) : (
            <>
              <div className={`st-value ${tone === 'alert' ? 'text-alert' : tone === 'warn' ? 'text-warn' : ''}`} style={tone === 'ok' ? { color: 'var(--accent-text)' } : undefined}>
                <span className="u">€</span>{fmtEur(abs)}
              </div>
              <div className="st-delta">{pct.toFixed(1)}% sui ricavi</div>
            </>
          )}
        </div>
        <div>
          <div className="st-label">Soglia minima</div>
          <div className="st-value">{Number(p.marginThreshold).toFixed(0)}<span className="u">%</span></div>
          {/* Senza costi non si puo' dire nemmeno se la soglia e' rispettata. */}
          <div className={`st-delta${cost > 0 && pct < Number(p.marginThreshold) ? ' text-alert' : ''}`}>
            {cost === 0 ? 'da verificare' : pct < Number(p.marginThreshold) ? 'sotto soglia' : 'rispettata'}
          </div>
        </div>
      </div>

      <div className="grid gap-6" style={{ gridTemplateColumns: '2fr 1fr' }}>
        <div className="card">
          <CardHd
            icon="chart"
            color="var(--accent)"
            title="Breakdown costi per categoria"
            action={<span className="mute text-[12px]">Totale € {fmtEur(breakdown?.total ?? 0)}</span>}
          />
          {/* Resta .tbl nuda: sta dentro una card, e la variante editoriale
              full-bleed serve fuori dalle card. tbl--card vale solo sotto i
              768px, dove diventa un elenco di schede. */}
          <table className="tbl tbl--card pj-nolink">
            <thead><tr><th>Categoria</th><th>Ripartizione</th><th className="num">Importo</th><th className="num">%</th></tr></thead>
            <tbody>
              {(breakdown?.items ?? []).map((b) => (
                <tr key={b.category}>
                  <td data-m="hd" className="font-medium">{categoryLabels[b.category] ?? b.category}</td>
                  <td data-m="row" data-l="Ripartizione">
                    <div className="barbar" style={{ width: 180 }}>
                      <span style={{ width: `${b.pct}%` }} />
                    </div>
                  </td>
                  <td data-m="key" data-l="Importo" className="num mono">€ {fmtEur(b.amount)}</td>
                  <td data-m="key" data-l="Quota" className="num mono mute">{b.pct}%</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        <div className="card">
          <CardHd icon="chart" color="var(--accent)" title="Forecast scenari" />
          <div className="card-bd space-y-2.5">
            {scenarios.map((s) => {
              const sTone = s.pct < 20 ? 'alert' : s.pct < 30 ? 'warn' : 'ok';
              return (
                <div key={s.name} className="p-2.5 rounded border border-line">
                  <div className="flex items-center justify-between mb-1">
                    <span className="font-semibold text-[13px]">{s.name}</span>
                    {/* % = metrica, non stato: niente pill; colore solo se sotto soglia */}
                    <span className={`mono text-[12px] font-semibold ${sTone === 'alert' ? 'text-alert' : sTone === 'warn' ? 'text-warn' : 'mute'}`}>{s.pct.toFixed(1)}%</span>
                  </div>
                  <div className="flex items-baseline gap-2">
                    <span className={`mono text-[16px] font-semibold ${sTone === 'alert' ? 'text-alert' : sTone === 'warn' ? 'text-warn' : ''}`} style={sTone === 'ok' ? { color: 'var(--accent-text)' } : undefined}>€ {fmtEur(s.abs)}</span>
                    <span className="mute text-[12px]">{s.note}</span>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </div>

      {/* Il dettaglio per persona (ore/tariffe) vive in Team & Ore: qui solo il totale */}
      <div className="card">
        <div className="card-bd flex items-center gap-3">
          <Icon name="users" size={14} className="mute" />
          <span className="">Costo team <b className="mono">€ {fmtEur((teamProgress ?? []).reduce((a, r) => a + (Number(r.cost) || 0), 0))}</b></span>
          <span className="spacer" />
          <button className="btn btn-sm btn-secondary" onClick={onGoTeam}>Dettaglio in Team & Ore <Icon name="chevronR" size={12} /></button>
        </div>
      </div>
    </div>
  );
}

// =====================================================================
// ACTIVITY
// =====================================================================
function ActivityTab({ p }: { p: ProjectFull }) {
  const [filter, setFilter] = useState<'all' | string>('all');
  const filters = ['all', 'TASK', 'COMMIT', 'BUDGET', 'COMM', 'ALERT', 'DOC', 'QA'];
  const filterLabels: Record<string, string> = { all: 'Tutte', TASK: 'Task', COMMIT: 'Commit', BUDGET: 'Budget', COMM: 'Comunicazioni', ALERT: 'Alert', DOC: 'Documenti', QA: 'QA' };

  const filtered = p.activity.filter((a) => filter === 'all' || a.kind === filter);

  const { data: stats } = useQuery({
    queryKey: ['project-stats', p.id],
    queryFn: () => api.get<{ daysActive: number; activitiesLast4w: number; commsTotal: number; emails: number; meetings: number; tasksDone: number; tasksOpen: number }>(`/projects/${p.id}/stats`),
  });

  return (
    <div className="grid gap-6" style={{ gridTemplateColumns: '2fr 1fr' }}>
      {/* Lista nuda: kicker + filtro a destra, niente card */}
      <div className="ed-section">
        <div className="data-hd">
          <div className="flex items-center gap-2">
            <span className="t-micro">Log attività</span>
            <span className="mono mute text-[12px]">{filtered.length}</span>
          </div>
          <select className="inp text-[12px] py-1" style={{ width: 150 }} value={filter} onChange={(e) => setFilter(e.target.value)}>
            {filters.map((f) => (
              <option key={f} value={f}>{filterLabels[f]}</option>
            ))}
          </select>
        </div>
        <div className="py-1">
          {filtered.map((a) => (
            <div key={a.id} className="activity-row">
              <div className={`activity-dot activity-${a.kind.toLowerCase()}`}>
                <Icon name={a.kind === 'TASK' ? 'check' : a.kind === 'ALERT' ? 'alert' : a.kind === 'DOC' ? 'file' : a.kind === 'BUDGET' ? 'euro' : a.kind === 'COMM' ? 'mail' : 'pin'} size={10} />
              </div>
              <div className="flex-1">
                <div className="">
                  <b>{a.actor ? `${a.actor.firstName[0]}. ${a.actor.lastName}` : 'Sistema'}</b> <span>{a.message}</span>
                </div>
                <div className="mute mono text-[12px] mt-0.5">{fmtDate(a.createdAt)}</div>
              </div>
            </div>
          ))}
          {!filtered.length && (
            <EmptyState
              icon="list"
              title="Nessuna attività"
              description="Le azioni sul progetto compariranno qui automaticamente."
            />
          )}
        </div>
      </div>

      {/* Colonna laterale: kicker-blocks nudi impilati, separati da hairline */}
      <div className="col">
        <div className="ed-section">
          <div className="data-hd">
            <span className="t-micro">Stats progetto</span>
          </div>
          <div className="ed-kv">
            <div className="ed-kv-row"><span className="k">Giorni attivi</span><span className="v mono">{stats?.daysActive ?? '—'}</span></div>
            <div className="ed-kv-row"><span className="k">Attività ultime 4 sett</span><span className="v mono">{stats?.activitiesLast4w ?? '—'}</span></div>
            <div className="ed-kv-row"><span className="k">Task completati</span><span className="v mono">{stats?.tasksDone ?? '—'}</span></div>
            <div className="ed-kv-row"><span className="k">Task aperti</span><span className="v mono">{stats?.tasksOpen ?? '—'}</span></div>
            <div className="ed-kv-row"><span className="k">Comunicazioni totali</span><span className="v mono">{stats?.commsTotal ?? '—'}</span></div>
            <div className="ed-kv-row"><span className="k">Email scambiate</span><span className="v mono">{stats?.emails ?? '—'}</span></div>
            <div className="ed-kv-row"><span className="k">Meeting cliente</span><span className="v mono">{stats?.meetings ?? '—'}</span></div>
          </div>
        </div>

        <div className="ed-divider" />

        <div className="ed-section">
          <div className="data-hd">
            <span className="t-micro">Automazioni attive</span>
          </div>
          <div>
            {(p.automations ?? []).map((a) => (
              <div key={a.id} className="flex items-center py-1.5">
                <span>{a.name}</span>
                <span className="spacer" />
                <Chip tone={a.enabled ? 'ok' : 'ghost'}>{a.enabled ? 'Attiva' : 'Off'}</Chip>
              </div>
            ))}
            {!p.automations?.length && (
              <div className="py-6 text-center text-[13px] mute">Nessuna automazione configurata</div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

// =====================================================================
// Shared
// =====================================================================
/**
 * Testata card "firma bento": icon-tile tinta + titolo (+ sottotitolo) a
 * sinistra, azione invariata a destra. Replica la grammatica della Dashboard.
 */
function CardHd({ icon, color, title, sub, action }: {
  icon: IconName; color?: string; title: React.ReactNode; sub?: React.ReactNode; action?: React.ReactNode;
}) {
  return (
    <div className="card-hd">
      <div className="flex items-center gap-3">
        <div className="icon-tile sm" style={{ ['--c' as any]: color ?? 'var(--accent)' }}><Icon name={icon} size={14} /></div>
        <div>
          <div className="t-h3">{title}</div>
          {sub != null && <div className="t-caption">{sub}</div>}
        </div>
      </div>
      {action}
    </div>
  );
}

